import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { GoogleGenerativeAI } from "@google/generative-ai";
import { 
  Container, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Grid, 
  Stack, 
  CircularProgress, 
  Alert, 
  IconButton,
  Skeleton,
  Divider,
  Chip,
  LinearProgress,
  useTheme,
  Tooltip,
  Modal,
  FormControlLabel, // Added
  Switch,           // Added
  FormControl,      // Added
  InputLabel,       // Added
  Select,           // Added
  MenuItem,         // Added
  Avatar,           // Added
  InputAdornment,   // Added for icon in TextField
  Fab               // Added for Floating Action Button style
} from '@mui/material';
import { styled, keyframes } from '@mui/system'; // Import keyframes
import { Fade, Grow, Slide, Collapse } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SendIcon from '@mui/icons-material/Send'; // Added for generate button
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'; // Import PDF icon
import EditIcon from '@mui/icons-material/Edit'; // Import Edit icon
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'; // Added
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'; // Added
import ReactMarkdown from 'react-markdown';
import jsPDF from 'jspdf'; // Import jsPDF
import CountUp from 'react-countup'; // Import CountUp
import { supabase } from '../supabaseClient';
import { consumeCredits } from '../supabaseClient';

// Styled components
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const ImagePreview = styled('img')(({ theme }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: theme.shape.borderRadius,
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'scale(1.05)',
  },
}));

const StepIndicator = styled(Box)(({ theme, isactive }) => ({
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: isactive === 'true'
    ? theme.palette.mode === 'dark' 
      ? theme.palette.primary.dark 
      : theme.palette.primary.light
    : theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.05)',
  color: isactive === 'true'
    ? theme.palette.primary.contrastText
    : theme.palette.text.secondary,
  transition: 'all 0.3s ease',
  transform: isactive === 'true' ? 'translateY(-2px)' : 'none',
  boxShadow: isactive === 'true' ? theme.shadows[3] : 'none',
}));

const SectionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: theme.shadows[4],
    transform: 'translateY(-2px)',
  },
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
  },
}));

// --- New Keyframes for Animations ---
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

// --- New Epic Title Animation ---
const epicTitleAnimation = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(20px); // Start slightly lower
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

// --- New Warm & Professional Title Animation ---
const warmProfessionalReveal = keyframes`
  from {
    opacity: 0;
    transform: scale(0.98) translateY(5px); // Start slightly smaller, lower, and invisible
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0); // End at normal size, position, and fully visible
  }
`;

const pulseGlow = (color) => keyframes`
  0% { box-shadow: 0 0 5px ${color}30, 0 0 10px ${color}20; }
  50% { box-shadow: 0 0 15px ${color}50, 0 0 25px ${color}30; }
  100% { box-shadow: 0 0 5px ${color}30, 0 0 10px ${color}20; }
`;

// Utility functions
const fixTurkishCharsForPdf = (text) => {
  return text
    .replace(/ı/g, 'i')
    .replace(/İ/g, 'I')
    .replace(/ğ/g, 'g')
    .replace(/Ğ/g, 'G')
    .replace(/ü/g, 'u')
    .replace(/Ü/g, 'U')
    .replace(/ş/g, 's')
    .replace(/Ş/g, 'S')
    .replace(/ö/g, 'o')
    .replace(/Ö/g, 'O')
    .replace(/ç/g, 'c')
    .replace(/Ç/g, 'C');
};

const base64ToBlob = (base64, mimeType) => {
  // Handle data URLs
  let base64Data = base64;
  if (base64.startsWith('data:')) {
    base64Data = base64.split(',')[1];
  }

  const byteCharacters = atob(base64Data);
  const byteArrays = [];

  for (let i = 0; i < byteCharacters.length; i += 512) {
    const slice = byteCharacters.slice(i, i + 512);
    const byteNumbers = new Array(slice.length);
    
    for (let j = 0; j < slice.length; j++) {
      byteNumbers[j] = slice.charCodeAt(j);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: mimeType });
};

// --- New Image Resizing Function ---
const resizeImage = (file, maxWidth = 1344) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Check if resizing is needed
        if (width > maxWidth) {
          const ratio = maxWidth / width;
          width = maxWidth;
          height = height * ratio;
          console.log(`Resizing image from ${img.width}x${img.height} to ${width}x${height}`);
        } else {
          console.log(`Image width (${img.width}px) is within the limit (${maxWidth}px). No resizing needed.`);
          // If no resizing is needed, resolve with the original file blob
          resolve(file);
          return;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convert canvas to blob
        canvas.toBlob((blob) => {
          if (blob) {
            // Create a new File object to preserve the name and type if needed,
            // though for base64 conversion, the blob itself is sufficient.
            // resolve(new File([blob], file.name, { type: file.type }));
            resolve(blob); // Resolve with the resized blob
          } else {
            reject(new Error('Canvas to Blob conversion failed'));
          }
        }, file.type || 'image/jpeg', 0.9); // Use original file type, quality 0.9
      };
      img.onerror = (error) => reject(error);
      img.src = event.target.result; // Set source after defining onload
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file); // Read the original file to get image data
  });
};


const fileToBase64 = (file) => {
  return new Promise(async (resolve, reject) => { // Make async
    try {
      // --- Resize the image first ---
      const resizedBlob = await resizeImage(file); // Wait for resizing

      // --- Read the potentially resized blob as Data URL ---
      const reader = new FileReader();
      reader.readAsDataURL(resizedBlob); // Read the resized blob
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    } catch (error) {
      console.error("Error during image resizing or base64 conversion:", error);
      reject(error);
    }
  });
};

// Room types, styles, and color palettes data
const roomTypes = [
  { value: 'oturma odası', label: 'Oturma Odası', icon: '🛋️' },
  { value: 'yatak odası', label: 'Yatak Odası', icon: '🛏️' },
  { value: 'mutfak', label: 'Mutfak', icon: '🍳' },
  { value: 'çalışma odası', label: 'Çalışma Odası', icon: '💼' },
  { value: 'banyo', label: 'Banyo', icon: '🚿' },
  { value: 'yemek odası', label: 'Yemek Odası', icon: '🍽️' },
  { value: 'giriş holü', label: 'Giriş Holü', icon: '🚪' },
  { value: 'çocuk odası', label: 'Çocuk Odası', icon: '🧸' }
];

const roomStyles = [
  { value: 'modern', label: 'Modern', icon: '🏢' },
  { value: 'iskandinav', label: 'İskandinav', icon: '❄️' },
  { value: 'endüstriyel', label: 'Endüstriyel', icon: '🏭' },
  { value: 'rustik', label: 'Rustik', icon: '🌳' },
  { value: 'minimalist', label: 'Minimalist', icon: '⬜' },
  { value: 'klasik', label: 'Klasik', icon: '🏛️' },
  { value: 'vintage', label: 'Vintage', icon: '📻' },
  { value: 'bohem', label: 'Bohem', icon: '🧵' }
];

// Furniture mapping by room type
const furnitureByRoomType = {
  'oturma odası': ['koltuk', 'sehpa', 'halı', 'aydınlatma', 'yastıklar', 'kitaplık', 'tv ünitesi', 'perde'],
  'yatak odası': ['yatak', 'komodin', 'gardırop', 'şifonyer', 'yatak örtüsü', 'başlık', 'perde', 'aydınlatma'],
  'mutfak': ['mutfak dolabı', 'ada', 'tabure', 'tezgah', 'lavabo', 'ocak', 'buzdolabı', 'bitki'],
  'çalışma odası': ['çalışma masası', 'ofis koltuğu', 'kitaplık', 'masa lambası', 'duvar rafları', 'bilgisayar', 'perde'],
  'banyo': ['duş kabini', 'küvet', 'lavabo', 'ayna', 'havluluk', 'banyo dolabı', 'aksesuar', 'bitki'],
  'yemek odası': ['yemek masası', 'sandalyeler', 'konsol', 'avize', 'vitrin', 'servis arabası', 'perde', 'tablo'],
  'giriş holü': ['konsol', 'ayna', 'portmanto', 'puf', 'ayakkabılık', 'duvar süsü', 'aydınlatma'],
  'çocuk odası': ['çocuk yatağı', 'çalışma masası', 'oyuncak kutusu', 'kitaplık', 'halı', 'perde', 'duvar çıkartmaları']
};

// Style-specific furniture attributes
const styleAttributes = {
  'modern': 'temiz çizgili, minimalist, parlak metal detaylı',
  'iskandinav': 'açık renkli ahşap, yumuşak dokulu, fonksiyonel',
  'endüstriyel': 'metal, açıkta kalan borular, ham ahşap, tuğla duvar',
  'rustik': 'doğal ahşap, el yapımı, dokulu kumaşlar, antika',
  'minimalist': 'sade, işlevsel, az sayıda eşya, geniş boş alanlar',
  'klasik': 'oymalı ahşap, kadife döşeme, zengin detaylar, simetrik',
  'vintage': 'retro, eskitilmiş, nostaljik, pastel renkler',
  'bohem': 'renkli, desenli, katmanlı dokular, bitkiler, makrame'
};

const colorPalettes = [
  { value: 'neutral', label: 'Nötr', colors: ['#F5F5F5', '#E0E0E0', '#9E9E9E', '#616161'] },
  { value: 'warm', label: 'Sıcak', colors: ['#FFEBEE', '#FFCDD2', '#EF9A9A', '#E57373'] },
  { value: 'cool', label: 'Soğuk', colors: ['#E3F2FD', '#BBDEFB', '#90CAF9', '#64B5F6'] },
  { value: 'earth', label: 'Toprak', colors: ['#EFEBE9', '#D7CCC8', '#BCAAA4', '#A1887F'] },
  { value: 'forest', label: 'Orman', colors: ['#E8F5E9', '#C8E6C9', '#A5D6A7', '#81C784'] },
  { value: 'sunset', label: 'Gün Batımı', colors: ['#FFF3E0', '#FFE0B2', '#FFCC80', '#FFB74D'] }
];

// Image Customization Modal Component (Moved outside DocumentGenerator)
const ImageCustomizationModal = ({
  open,
  onClose,
  roomTypes,
  roomStyles,
  colorPalettes,
  selectedRoomType,
  setSelectedRoomType,
  selectedRoomStyle,
  setSelectedRoomStyle,
  selectedColorPalette,
  setSelectedColorPalette,
  onApply,
  isApplying // Renamed from isRerollingImage for clarity in modal context
}) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="image-customization-modal"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '90%', sm: 500 },
        bgcolor: 'background.paper',
        borderRadius: 2,
        boxShadow: 24,
        p: 4,
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Görsel Özelleştirme
        </Typography>
        
        <Divider sx={{ mb: 3 }} />
        
        <Typography variant="subtitle2" gutterBottom>
          Oda Tipi
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 1, 
          mb: 3,
          justifyContent: 'center' 
        }}>
          {roomTypes.map((type) => (
            <Chip
              key={type.value}
              label={`${type.icon} ${type.label}`}
              onClick={() => setSelectedRoomType(type.value)}
              color={selectedRoomType === type.value ? "primary" : "default"}
              sx={{ 
                transition: 'all 0.2s ease',
                transform: selectedRoomType === type.value ? 'scale(1.05)' : 'scale(1)',
                fontWeight: selectedRoomType === type.value ? 'bold' : 'normal',
                '&:hover': {
                  transform: 'scale(1.05)',
                }
              }}
            />
          ))}
        </Box>
        
        <Typography variant="subtitle2" gutterBottom>
          Oda Stili
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 1, 
          mb: 3,
          justifyContent: 'center'
        }}>
          {roomStyles.map((style) => (
            <Chip
              key={style.value}
              label={`${style.icon} ${style.label}`}
              onClick={() => setSelectedRoomStyle(style.value)}
              color={selectedRoomStyle === style.value ? "primary" : "default"}
              sx={{ 
                transition: 'all 0.2s ease',
                transform: selectedRoomStyle === style.value ? 'scale(1.05)' : 'scale(1)',
                fontWeight: selectedRoomStyle === style.value ? 'bold' : 'normal',
                '&:hover': {
                  transform: 'scale(1.05)',
                }
              }}
            />
          ))}
        </Box>
        
        <Typography variant="subtitle2" gutterBottom>
          Renk Paleti
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 2, 
          mb: 3,
          justifyContent: 'center'
        }}>
          {colorPalettes.map((palette) => (
            <Box 
              key={palette.value}
              onClick={() => setSelectedColorPalette(palette.value)}
              sx={{ 
                cursor: 'pointer',
                borderRadius: 2,
                padding: 0.5,
                border: '2px solid',
                borderColor: selectedColorPalette === palette.value ? 'primary.main' : 'transparent',
                transition: 'all 0.2s ease',
                transform: selectedColorPalette === palette.value ? 'scale(1.05)' : 'scale(1)',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: 2
                }
              }}
            >
              <Typography variant="caption" align="center" display="block" sx={{ mb: 1 }}>
                {palette.label}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {palette.colors.map((color, i) => (
                  <Box 
                    key={i}
                    sx={{ 
                      width: 20, 
                      height: 20, 
                      bgcolor: color,
                      borderRadius: '50%'
                    }}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
          <Button 
            variant="outlined" 
            onClick={onClose}
          >
            İptal
          </Button>
          <Button 
            variant="contained" 
            onClick={onApply}
            disabled={isApplying}
            startIcon={isApplying ? <CircularProgress size={20} /> : null}
          >
            {isApplying ? 'Oluşturuluyor...' : 'Görseli Yeniden Oluştur'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

// Main component
const DocumentGenerator = () => {
  const theme = useTheme();
  
  // Initialize Gemini clients
  const genAI = useMemo(() => new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY), []);
  const textModel = useMemo(() => genAI.getGenerativeModel({ 
    model: "gemini-2.0-flash",
    safetySettings: [
      {
        category: "HARM_CATEGORY_HARASSMENT",
        threshold: "BLOCK_ONLY_HIGH"
      },
      {
        category: "HARM_CATEGORY_HATE_SPEECH",
        threshold: "BLOCK_ONLY_HIGH"
      },
      {
        category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
        threshold: "BLOCK_ONLY_HIGH"
      },
      {
        category: "HARM_CATEGORY_DANGEROUS_CONTENT",
        threshold: "BLOCK_ONLY_HIGH"
      }
    ]
  }), [genAI]);
  const imageModel = useMemo(() => genAI.getGenerativeModel({ model: "gemini-2.0-flash-exp-image-generation" }), [genAI]);
  
  // State management
  const [sourceImages, setSourceImages] = useState([]);
  const [userRequest, setUserRequest] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [documentSections, setDocumentSections] = useState([]);
  const [analysisSummary, setAnalysisSummary] = useState(null);
  const [globalError, setGlobalError] = useState(null);
  const [generatedBlobUrls, setGeneratedBlobUrls] = useState(new Set());
  const [progress, setProgress] = useState(0);
  const [session, setSession] = useState(null);
  const [creditsLeft, setCreditsLeft] = useState(0);
  const [showCreditWarning, setShowCreditWarning] = useState(false);
  const [showPlanApproval, setShowPlanApproval] = useState(false);
  const [plannedSections, setPlannedSections] = useState([]);
  const [plannedSectionSettings, setPlannedSectionSettings] = useState([]);
  
  // Image customization states
  const [imageCustomizationOpen, setImageCustomizationOpen] = useState(false);
  const [currentCustomizingSection, setCurrentCustomizingSection] = useState(null);
  const [selectedRoomType, setSelectedRoomType] = useState('oturma odası');
  const [selectedRoomStyle, setSelectedRoomStyle] = useState('modern');
  const [selectedColorPalette, setSelectedColorPalette] = useState('neutral');
  const [isRerollingImage, setIsRerollingImage] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false); // State for PDF download
  const [userLogos, setUserLogos] = useState([]);
  const [selectedLogoUrl, setSelectedLogoUrl] = useState('');
  const [includeLogoInExport, setIncludeLogoInExport] = useState(false);
  const [logoLoading, setLogoLoading] = useState(false);
  const [documentTitle, setDocumentTitle] = useState("Mimari Blog"); // State for main title
  const [isEditingTitle, setIsEditingTitle] = useState(false); // State for title editing mode
  const [editingElement, setEditingElement] = useState(null); // { sectionId: string, field: 'title' | 'content' } | null
  const [previousCredits, setPreviousCredits] = useState(0); // State for animation start
  const [isAnimatingCredits, setIsAnimatingCredits] = useState(false); // State for flash effect trigger
  const [totalCreditsConsumedThisSession, setTotalCreditsConsumedThisSession] = useState(0); // Track total cost
  const [showTotalConsumedMessage, setShowTotalConsumedMessage] = useState(false); // Control total cost message visibility

  // Refs
  const fileInputRef = useRef(null);
  const resultContainerRef = useRef(null);

  // Get user session
  useEffect(() => {
    const getSession = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
      
      if (data.session?.user?.id) {
        // Get user credits from profiles table
        const { data: userProfile, error } = await supabase
          .from('profiles')
          .select('credits_remaining')
          .eq('id', data.session.user.id)
          .single();
          
        if (!error && userProfile) {
          setCreditsLeft(userProfile.credits_remaining);
        }
      }
      // Return the session data so the .then() callback receives it
      return data; 
    };
    
    getSession().then((sessionData) => { // Now sessionData is the object { session: ... }
      // Fetch user logos if session exists
      if (sessionData?.session?.user?.id) {
        fetchUserLogos(sessionData.session.user.id);
      }
    });
  }, []);

  const fetchUserLogos = async (userId) => {
    try {
      setLogoLoading(true);
      const { data, error } = await supabase
        .from('user_logos')
        .select('id, logo_name, logo_url')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUserLogos(data || []);
      // Select the first logo by default if logos exist
      if (data && data.length > 0) {
        setSelectedLogoUrl(data[0].logo_url);
      }
    } catch (err) {
      console.error('Error fetching user logos:', err);
      // Optionally set an error state here
    } finally {
      setLogoLoading(false);
    }
  };

  // Cleanup blob URLs on unmount
  useEffect(() => {
    const urlsToRevoke = generatedBlobUrls;
    return () => {
      console.log("Cleaning up Blob URLs...");
      urlsToRevoke.forEach(url => {
        try {
          URL.revokeObjectURL(url);
          console.log("Revoked:", url);
        } catch (e) {
          console.error("Error revoking URL:", url, e);
        }
      });
    };
  }, []); // Empty dependency array to only run on unmount

  // --- Effect to reset credit animation trigger ---
  useEffect(() => {
    if (isAnimatingCredits) {
      const timer = setTimeout(() => setIsAnimatingCredits(false), 600); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [isAnimatingCredits]);

  // --- State Update Handler for Inline Editing ---
  const handleSectionUpdate = (sectionId, field, value) => {
    setDocumentSections(prevSections =>
      prevSections.map(section =>
        section.id === sectionId ? { ...section, [field]: value } : section
      )
    );
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      // Only accept image files
      const imageFiles = files.filter(file => file.type.startsWith('image/'));
      
      // Limit to 4 images total
      const availableSlots = 4 - sourceImages.length;
      const filesToAdd = imageFiles.slice(0, availableSlots);
      
      if (filesToAdd.length > 0) {
        setSourceImages(prev => [...prev, ...filesToAdd]);
      }
      
      // Show warning if user tried to upload more than allowed
      if (imageFiles.length > availableSlots) {
        setGlobalError("Maksimum 4 görsel yükleyebilirsiniz. Bazı görseller eklenmedi.");
        setTimeout(() => setGlobalError(null), 3000);
      }
    }
  };

  // Remove an image
  const handleRemoveImage = (index) => {
    setSourceImages(prev => prev.filter((_, i) => i !== index));
  };

  // --- Function to remove a planned section ---
  const handleRemovePlannedSection = (indexToRemove) => {
    setPlannedSections(prevSections => prevSections.filter((_, index) => index !== indexToRemove));
    setPlannedSectionSettings(prevSettings => prevSettings.filter((_, index) => index !== indexToRemove));
  };

  // --- Helper function to consume credits and trigger animation ---
  const consumeAndAnimateCredits = async (amount) => {
    if (!session?.user?.id) {
      console.warn("User not logged in, cannot consume credits.");
      console.warn("User not logged in, cannot consume credits.");
      setGlobalError("Kullanıcı girişi yapılmamış, kredi kullanılamıyor.");
      setShowCreditWarning(true);
      return false; // Indicate failure
    }

    // *** Check credits BEFORE trying to update ***
    if (creditsLeft < amount) {
      console.warn(`Insufficient credits. Needed: ${amount}, Available: ${creditsLeft}`);
      setGlobalError(`Yetersiz kredi. Gerekli: ${amount}, Mevcut: ${creditsLeft}`);
      setShowCreditWarning(true);
      return false; // Indicate failure
    }

    const newCredits = Math.max(0, creditsLeft - amount); // Calculate potential new credit amount

    try {
      // --- Attempt to update credits in the database ---
      setPreviousCredits(creditsLeft); // Store current value *before* DB update for animation
      const { data, error } = await supabase
        .from('profiles')
        .update({ credits_remaining: newCredits })
        .eq('id', session.user.id)
        .select('credits_remaining')
        .single();

      if (error) {
        console.error(`Error updating credits in database for user ${session.user.id}:`, error);
        throw error; // Re-throw the error to be caught below
      }

      // --- Update local state ONLY after successful DB update ---
      if (data && typeof data.credits_remaining === 'number') {
        setCreditsLeft(data.credits_remaining); // Use the confirmed value from DB
        setIsAnimatingCredits(true); // Trigger flash animation
        setTotalCreditsConsumedThisSession(prev => prev + amount); // Increment total consumed
        console.log(`Successfully consumed ${amount} credits. Total this session: ${totalCreditsConsumedThisSession + amount}. Credits left: ${data.credits_remaining}`);
        return true; // Indicate success
      } else {
        // This case should ideally not happen if the update was successful and returned data
        console.warn("Credit update successful but didn't return expected data. Using calculated value locally.");
        setCreditsLeft(newCredits); // Fallback to calculated value, but log a warning
        setIsAnimatingCredits(true);
        setTotalCreditsConsumedThisSession(prev => prev + amount);
        return true; // Still indicate success as DB was likely updated
      }

    } catch (error) {
      // Catch errors from the database update attempt
      console.error(`Failed to consume ${amount} credits for user ${session.user.id}:`, error);
      setGlobalError(`Kredi düşülürken bir veritabanı hatası oluştu: ${error.message}`);
      setShowCreditWarning(true); // Show warning as the operation failed
      // Do NOT update local credit state if DB update failed
      return false; // Indicate failure
    }
  };


  // Generate image for a section with retry logic
  // NOTE: Credit consumption is now handled by the calling function (continueDocumentGeneration or regenerateSectionImage)
  const generateImageForSection = async (sectionId, imageDescription, isRegeneration = false, customOptions = null) => {
    // --- Proceed with Image Generation (Credits assumed to be pre-consumed) ---
    // Update section status to show we're attempting generation
    setDocumentSections(prev => prev.map(s => 
      s.id === sectionId 
        ? { ...s, imageStatus: 'generating', error: null } // Clear previous errors
        : s
    ));
    
    // If we're rerolling with custom options, update the state
    if (isRegeneration && customOptions) {
      setIsRerollingImage(true);
    }
    
    // Retry configuration
    const maxRetries = 3;
    const baseDelay = 2000; // Start with 2 second delay
    
    // Retry function with exponential backoff
    const attemptWithRetry = async (attempt = 1) => {
      try {
        // Use custom options if provided, otherwise use section's stored settings
        let roomType, roomStyle, colorPalette;
        
        if (customOptions) {
          roomType = customOptions.roomType;
          roomStyle = customOptions.roomStyle;
          colorPalette = customOptions.colorPalette;
        } else {
          // Get the section's stored settings
          const section = documentSections.find(s => s.id === sectionId);
          
          if (section && section.roomType) {
            // Use the section's stored settings
            roomType = section.roomType;
            roomStyle = section.roomStyle;
            colorPalette = section.colorPalette;
          } else {
            // Extract room type and style from description if possible, or use defaults
            roomType = selectedRoomType; // Use the selected default
            roomStyle = selectedRoomStyle; // Use the selected default
            colorPalette = selectedColorPalette; // Use the selected default
            
            // Try to identify room type from description
            for (const type of roomTypes) {
              if (imageDescription.toLowerCase().includes(type.value)) {
                roomType = type.value;
                break;
              }
            }
            
            // Try to identify style from description
            for (const style of roomStyles) {
              if (imageDescription.toLowerCase().includes(style.value)) {
                roomStyle = style.value;
                break;
              }
            }
          }
        }
        
        // Get color palette description
        const colorDesc = colorPalette === 'neutral' ? '' : 
                         `${colorPalettes.find(p => p.value === colorPalette)?.label} renk tonlarında, `;
        
        // Get appropriate furniture for the room type
        const roomFurniture = furnitureByRoomType[roomType] || furnitureByRoomType['oturma odası'];
        
        // Use ALL furniture items appropriate for this room type
        const allFurniture = roomFurniture.join(', ');
        
        // Get style attributes
        const styleAttribute = styleAttributes[roomStyle] || '';
        
        // Create a simplified, direct prompt following the example structure
        const enhancedPrompt = `Edit this image of ${roomType}. Add ${roomStyle} ${roomType.toLowerCase()} furniture: ${allFurniture}. ${colorDesc ? `Use ${colorDesc} colors. ` : ''}`;
        
        // Log the prompt for debugging
        console.log('Image generation prompt:', enhancedPrompt);
        
        // Create content parts array starting with the text prompt
        const contentParts = [{ text: enhancedPrompt }];
        
        // Use user-uploaded images if available - with deterministic selection based on section ID
        if (sourceImages.length > 0) {
          try {
            // Use section ID to deterministically select an image
            // This ensures each section uses a different image in a predictable way
            const sectionIndex = parseInt(sectionId.replace('sec-', ''));
            const imageIndex = sectionIndex % sourceImages.length;
            const sourceImage = sourceImages[imageIndex];
            
            console.log(`Using image ${imageIndex + 1} of ${sourceImages.length} for section ${sectionId}`);

            // --- Resize the source image before converting ---
            const resizedBlob = await resizeImage(sourceImage); // Resize the selected source image

            // --- Convert the resized blob to base64 ---
            const base64Data = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                const base64String = reader.result;
                // Ensure result is a string and split correctly
                if (typeof base64String === 'string' && base64String.includes(',')) {
                  const base64 = base64String.split(',')[1];
                  resolve(base64);
                } else {
                  reject(new Error('Failed to read resized image as base64 data URL.'));
                }
              };
              reader.onerror = (error) => reject(error);
              reader.readAsDataURL(resizedBlob); // Read the resized blob
            });

            // Add the image to content parts using the resized blob's type
            contentParts.push({
              inlineData: {
                mimeType: resizedBlob.type, // Use mimeType from the resized blob
                data: base64Data
              }
            });
            
            console.log('Using user-uploaded image for generation');
          } catch (imageError) {
            console.error('Error processing user image:', imageError);
            // Continue without the image if processing fails
          }
        } else {
          console.log('No user images available for generation');
        }
        
        // Use only the user role which is supported by the image generation model
        const contents = [
          { role: "user", parts: contentParts }
        ];

        // Set a timeout to prevent hanging requests
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error("Image generation timed out after 30 seconds")), 30000)
        );

        // Update UI to show retry attempt if not the first try
        if (attempt > 1) {
          setDocumentSections(prev => prev.map(s => 
            s.id === sectionId 
              ? { 
                  ...s, 
                  imageStatus: 'generating',
                  error: `Retry attempt ${attempt}/${maxRetries}...` 
                } 
              : s
          ));
        }

        // Generate the image with timeout
        const resultPromise = imageModel.generateContent({
          contents: contents,
          generationConfig: {
            responseModalities: ['TEXT', 'IMAGE'],
            temperature: 0.2,
            topK: 1, // Increased topK for potentially more diverse image results
            topP: 0.95,
          },
        });

        // Race between the generation and the timeout
        const result = await Promise.race([resultPromise, timeoutPromise]);
      
        // Process the response
        const response = result.response;
        
        if (response && response.candidates && response.candidates.length > 0) {
          const candidate = response.candidates[0];
          if (candidate.content && candidate.content.parts) {
            // First, check if there's any text in the response that might be helpful
            let responseText = '';
            for (const part of candidate.content.parts) {
              if (part.text) {
                responseText = part.text;
                console.log('Response text from image generation:', responseText);
              }
            }
            
            // Then look for image data
            for (const part of candidate.content.parts) {
              if (part.inlineData && part.inlineData.data) {
                // This is the base64 image data
                const imageData = part.inlineData.data;
                const mimeType = part.inlineData.mimeType || 'image/png';
                
                // Convert base64 to Blob
                const blob = base64ToBlob(`data:${mimeType};base64,${imageData}`, mimeType);
                
                // Create Blob URL
                const blobUrl = URL.createObjectURL(blob);
                
                // Track Blob URL
                setGeneratedBlobUrls(prev => new Set([...prev, blobUrl]));
                
                // Update section state
                setDocumentSections(prev => prev.map(s => 
                  s.id === sectionId 
                    ? { 
                        ...s, 
                        imageUrl: blobUrl, 
                        imageStatus: 'completed', 
                        error: null,
                        roomType: customOptions?.roomType || selectedRoomType,
                        roomStyle: customOptions?.roomStyle || selectedRoomStyle,
                        colorPalette: customOptions?.colorPalette || selectedColorPalette
                      } 
                    : s
                ));
                
                // Reset rerolling state
                setIsRerollingImage(false);
                
                return true; // Success
              }
            }
            
            // If we found text but no image, include the text in the error message
            if (responseText) {
              throw new Error(`No image data found in the response. Model message: ${responseText.substring(0, 100)}...`);
            }
          }
        }
        
        // If we get here, no image was found in the response
        throw new Error("No image data found in the response. The model may be having difficulty generating an image for this description.");
        
      } catch (error) {
        console.error(`Image generation attempt ${attempt} failed:`, error);
        
        // Check if we should retry
        if (attempt < maxRetries) {
          // Calculate exponential backoff delay
          const delay = baseDelay * Math.pow(2, attempt - 1);
          console.log(`Retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})...`);
          
          // Update UI to show waiting status
          setDocumentSections(prev => prev.map(s => 
            s.id === sectionId 
              ? { 
                  ...s, 
                  imageStatus: 'generating',
                  error: `Retry in ${delay/1000}s (attempt ${attempt}/${maxRetries} failed: ${error.message})` 
                } 
              : s
          ));
          
          // Wait before retrying
          await new Promise(res => setTimeout(res, delay));
          
          // Try again with next attempt
          return attemptWithRetry(attempt + 1);
        }
        
        // If we've exhausted all retries, try with a significantly different approach as last resort
        try {
          console.log("All retries failed. Attempting with a significantly different approach as last resort...");
            
          // Update UI
          setDocumentSections(prev => prev.map(s => 
            s.id === sectionId 
              ? { 
                  ...s, 
                  imageStatus: 'generating',
                  error: "Trying completely different approach as last resort..." 
                } 
              : s
          ));
          
          // Create a more natural last resort prompt
          const roomTypes = ['yatak odası', 'oturma odası', 'mutfak', 'çalışma odası', 'banyo'];
          const roomStyles = ['modern', 'iskandinav', 'endüstriyel', 'rustik', 'minimalist', 'klasik', 'vintage', 'bohem'];
          
          // Extract room type and style from description if possible, or use defaults
          let roomType = roomTypes[0]; // Default to bedroom
          let roomStyle = roomStyles[1]; // Default to Scandinavian
          
          // Try to identify room type from description
          for (const type of roomTypes) {
            if (imageDescription.toLowerCase().includes(type)) {
              roomType = type;
              break;
            }
          }
          
          // Try to identify style from description
          for (const style of roomStyles) {
            if (imageDescription.toLowerCase().includes(style)) {
              roomStyle = style;
              break;
            }
          }
          
          // Get appropriate furniture for the room type
          const roomFurniture = furnitureByRoomType[roomType] || furnitureByRoomType['oturma odası'];
          
          // Select 3-4 different random furniture items appropriate for this room type
          const selectedFurniture = roomFurniture
            .sort(() => 0.5 - Math.random())
            .slice(0, 3)
            .join(', ');
          
          // Get style attributes
          const styleAttribute = styleAttributes[roomStyle] || '';
          
          // Create a simplified, direct prompt for the last resort attempt
          const lastResortPrompt = `Edit this image of ${roomType}. Add ${roomStyle} ${roomType.toLowerCase()} furniture: ${selectedFurniture}. `;
          
          // Log the last resort prompt for debugging
          console.log('Last resort image generation prompt:', lastResortPrompt);
          
          // Create content parts for last resort
          const lastResortContentParts = [{ text: lastResortPrompt }];
          
          // Use user-uploaded images if available for last resort attempt - with deterministic selection
          if (sourceImages.length > 0) {
            try {
              // Use the same source image as the primary attempt for consistency.
              const sectionIndex = parseInt(sectionId.replace('sec-', ''));
              // Calculate the intended image index for this section.
              const imageIndex = sectionIndex % sourceImages.length;
              const sourceImage = sourceImages[imageIndex];

              console.log(`Using image ${imageIndex + 1} of ${sourceImages.length} for last resort attempt on section ${sectionId}`);

              // --- Resize the source image before converting ---
              const resizedBlob = await resizeImage(sourceImage); // Resize the selected source image

              // --- Convert the resized blob to base64 ---
              const base64Data = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                  const base64String = reader.result;
                  // Ensure result is a string and split correctly
                  if (typeof base64String === 'string' && base64String.includes(',')) {
                    const base64 = base64String.split(',')[1];
                    resolve(base64);
                  } else {
                    reject(new Error('Failed to read resized image as base64 data URL for last resort.'));
                  }
                };
                reader.onerror = (error) => reject(error);
                reader.readAsDataURL(resizedBlob); // Read the resized blob
              });

              // Add the image to content parts using the resized blob's type
              lastResortContentParts.push({
                inlineData: {
                  mimeType: resizedBlob.type, // Use mimeType from the resized blob
                  data: base64Data
                }
              });
              
              console.log('Using user-uploaded image for last resort generation');
            } catch (imageError) {
              console.error('Error processing user image for last resort:', imageError);
              // Continue without the image if processing fails
            }
          } else {
            console.log('No user images available for last resort generation');
          }
          
          const lastResortContents = [
            { role: "user", parts: lastResortContentParts }
          ];
          
          const result = await imageModel.generateContent({
            contents: lastResortContents,
            generationConfig: {
              responseModalities: ['TEXT', 'IMAGE'],
              temperature: 0.2,
              topK: 1,
              topP: 0.95,
            },
          });
          
          const response = result.response;
          
          if (response && response.candidates && response.candidates.length > 0) {
            const candidate = response.candidates[0];
            if (candidate.content && candidate.content.parts) {
              for (const part of candidate.content.parts) {
                if (part.inlineData && part.inlineData.data) {
                  // This is the base64 image data
                  const imageData = part.inlineData.data;
                  const mimeType = part.inlineData.mimeType || 'image/png';
                  
                  // Convert base64 to Blob
                  const blob = base64ToBlob(`data:${mimeType};base64,${imageData}`, mimeType);
                  
                  // Create Blob URL
                  const blobUrl = URL.createObjectURL(blob);
                  
                  // Track Blob URL
                  setGeneratedBlobUrls(prev => new Set([...prev, blobUrl]));
                  
                  // Update section state
                  setDocumentSections(prev => prev.map(s => 
                    s.id === sectionId 
                      ? { ...s, imageUrl: blobUrl, imageStatus: 'completed', error: null } 
                      : s
                  ));
                  
                  return true; // Success with simplified prompt
                }
              }
            }
          }
          
          throw new Error("No image data found in the simplified prompt response");
        } catch (lastResortError) {
          console.error('Error in last resort image generation:', lastResortError);
          
          // Update section state with final error
          setDocumentSections(prev => prev.map(s => 
            s.id === sectionId 
              ? { 
                  ...s, 
                  imageStatus: 'error', 
                  error: `Image generation failed after multiple attempts: ${error.message}` 
                } 
              : s
          ));
          
          return false; // Failed all attempts
        }
      }
    };
    
    // Start the retry process
    return attemptWithRetry();
  };

  // Regenerate a specific section's text content
  const regenerateSectionText = async (sectionId) => {
    const section = documentSections.find(s => s.id === sectionId);
    if (!section) return;

    // --- Reroll Credit Check ---
    const rerollCost = 2; // Reduced from 5
    if (creditsLeft < rerollCost) {
      console.warn(`Not enough credits for text regeneration (Section ${sectionId}). Needed: ${rerollCost}, Available: ${creditsLeft}`);
      setDocumentSections(prev => prev.map(s => 
        s.id === sectionId 
          ? { ...s, status: 'error', error: `Metin yeniden oluşturma için yeterli kredi yok (${rerollCost} kredi gerekli).` } 
          : s
      ));
      return; // Stop if not enough credits
    }

    // --- Consume Credits using helper ---
    const consumed = await consumeAndAnimateCredits(rerollCost);
    // *** Explicitly check if consumption failed and stop ***
    if (!consumed) {
      console.error(`Credit consumption failed for text regeneration (Section ${sectionId}). Halting regeneration.`);
      // Error state should already be set by consumeAndAnimateCredits or the initial check
       setDocumentSections(prev => prev.map(s =>
        s.id === sectionId
          ? { ...s, status: 'error', error: globalError || `Metin yeniden oluşturma kredileri düşülemedi.` }
          : s
      ));
      return; // Stop if credit consumption fails
    }
    
    // --- Proceed with Text Regeneration ---
    // Update section status
    setDocumentSections(prev => prev.map(s => 
      s.id === sectionId 
        ? { ...s, status: 'text_generating', error: null } 
        : s
    ));
    
    try {
      // Get section index
      const sectionIndex = documentSections.findIndex(s => s.id === sectionId);
      const totalSections = documentSections.length;
      const currentSectionNumber = sectionIndex + 1;
      let sectionRole = "orta bölüm";
      if (currentSectionNumber === 1 && totalSections > 1) sectionRole = "giriş bölümü";
      if (currentSectionNumber === totalSections && totalSections > 1) sectionRole = "sonuç bölümü";

      // Construct text prompt with flow and conciseness instructions for regeneration
      const textPrompt = `"${section.title}" başlıklı mimari bir makale için ${totalSections} bölümlük bir yazının ${currentSectionNumber}. bölümünü (${sectionRole}) **yeniden** yazın. Bu bölümü ilgi çekici ve sohbet tarzında oluşturun.

      ${analysisSummary ? `Görsel Analizi Bağlamı: ${analysisSummary}` : ''}

      Kullanıcı İsteği: ${userRequest}

      ${sectionIndex > 0 ? `Önceki Bölümlerin İçeriği:\n${documentSections.slice(0, sectionIndex).map(s => `## ${s.title}\n${s.textContent}`).join('\n\n')}\n---\n` : ''}

      Önemli Talimatlar:
      1. **Akış ve Bağlam:** Bu bölümü, önceki içeriğin doğal bir devamı olarak yazın. Tekrarlayan giriş/sonuç ifadelerinden ("Sonuç olarak", "Özetle" vb.) kaçının, ancak bu bölüm **sonuç bölümü** ise uygun bir kapanış yapın.
      2. **Ton:** Profesyonel bir mimarın okuyucuyla konuştuğu gibi doğal, samimi bir ton kullanın.
      3. **Görsel İşareti:** Metinde uygun bir yere, görsel yerleştirileceğini belirtmek için [GÖRSEL: bu bölümü zenginleştirecek bir görselin kısa açıklaması] işaretini ekleyin.
      4. **Kısalık ve Biçim:** İçeriği özlü tutun (yaklaşık 150-200 kelime) ve 2-3 paragrafı geçmemeye çalışın. Vurgu için markdown kullanın.
      5. **Teknik Dil:** Aşırı teknik jargondan kaçının.
      6. **Kişisel Dokunuş:** "Genellikle şunu buluyoruz" gibi ifadelerle metni daha insancıl hale getirin.
      7. **Dil:** Metni Türkçe olarak yazın.`;

      let currentSectionText = '';
      
      try {
        let streamSuccess = false;
        
        try {
          // First try streaming approach
          console.log(`Starting streaming for section ${section.title} (regeneration)`);
          const result = await textModel.generateContentStream({
            contents: [{ role: "user", parts: [{ text: textPrompt }] }],
            generationConfig: {
              temperature: 0.7,
              topK: 3,
              topP: 0.95,
              maxOutputTokens: 4096,
            },
          });
            
          // Process the stream with controlled pacing
          const stream = result.stream;
          if (!stream) throw new Error("Stream not available");
            
          for await (const chunk of stream) {
            const chunkText = chunk.text();
            currentSectionText += chunkText;
              
            // Update UI with streaming text
            setDocumentSections(prev => prev.map(s => 
              s.id === sectionId 
                ? { ...s, textContent: currentSectionText } 
                : s
            ));
              
            // Longer delay to make the streaming more visible and readable
            await new Promise(res => setTimeout(res, 50));
          }
            
          streamSuccess = true;
          console.log(`Streaming completed successfully for section ${section.title} (regeneration)`);
        } catch (streamError) {
          console.warn(`Streaming failed for section ${section.title} (regeneration):`, streamError);
          streamSuccess = false;
        }
        
        // If streaming failed or produced no content, fall back to non-streaming
        if (!streamSuccess || !currentSectionText.trim()) {
          console.log(`Falling back to non-streaming for section ${section.title} (regeneration)`);
          // Show user that we're switching to non-streaming mode
          setDocumentSections(prev => prev.map(s => 
            s.id === sectionId 
              ? { 
                  ...s, 
                  textContent: "Streaming failed. Switching to standard generation mode...",
                  status: 'text_generating'
                } 
              : s
          ));
          
          // Wait a moment to show the message
          await new Promise(res => setTimeout(res, 1000));
          
          try {
            // Fallback to non-streaming approach with a more robust error handling
            const result = await textModel.generateContent({
              contents: [{ role: "user", parts: [{ text: textPrompt }] }],
              generationConfig: {
                temperature: 0.7,
                topK: 3,
                topP: 0.95,
                maxOutputTokens: 4096,
              },
            });
            
            if (result && result.response) {
              currentSectionText = result.response.text();
              console.log(`Non-streaming generation successful for section ${section.title} (regeneration)`);
              
              // Simulate streaming for better UX even in fallback mode
              let displayedText = "";
              const words = currentSectionText.split(" ");
              
              for (let i = 0; i < words.length; i += 3) { // Show 3 words at a time
                const chunk = words.slice(i, i + 3).join(" ") + " ";
                displayedText += chunk;
                
                // Update UI with the simulated streaming text
                setDocumentSections(prev => prev.map(s => 
                  s.id === sectionId 
                    ? { ...s, textContent: displayedText } 
                    : s
                ));
                
                // Delay between chunks to simulate typing
                await new Promise(res => setTimeout(res, 100));
              }
              
              // Final update with complete text
              setDocumentSections(prev => prev.map(s => 
                s.id === sectionId 
                  ? { ...s, textContent: currentSectionText } 
                  : s
              ));
            } else {
              throw new Error("Empty response from non-streaming generation");
            }
          } catch (nonStreamingError) {
            console.error(`Non-streaming generation also failed for section ${section.title} (regeneration):`, nonStreamingError);
            
            // Last resort: Generate a simple placeholder text
            currentSectionText = `Bu bölüm ${section.title} konusunu ele alacaktır. 
            
            İçerik genellikle mimari öğeler, tasarım düşünceleri ve projenin bu yönüyle ilgili teknik özellikler hakkında ayrıntılı bilgiler içerecektir.
            
            [GÖRSEL: ${section.title} ile ilgili profesyonel bir mimari görselleştirme]
            
            Daha spesifik içeriğe ihtiyacınız varsa lütfen bu bölümü yeniden oluşturun.`;
            
            setDocumentSections(prev => prev.map(s => 
              s.id === sectionId 
                ? { 
                    ...s, 
                    textContent: currentSectionText,
                    error: `Generation failed: ${nonStreamingError.message}. Using placeholder content.`
                  } 
                : s
            ));
          }
        }
      } catch (error) {
        console.error(`Unexpected error in text generation for section ${section.title} (regeneration):`, error);
        
        // Ensure we have some content even if everything fails
        if (!currentSectionText.trim()) {
          currentSectionText = `This section will cover ${section.title}. 
          
          The content would typically include detailed information about architectural elements, design considerations, and technical specifications related to this aspect of the project.
          
          [IMAGE: A professional architectural visualization related to ${section.title}]
          
          Please regenerate this section if you need more specific content.`;
          
          setDocumentSections(prev => prev.map(s => 
            s.id === sectionId 
              ? { 
                  ...s, 
                  textContent: currentSectionText,
                  error: `Generation failed: ${error.message}. Using placeholder content.`
                } 
              : s
          ));
        }
      }
      
      // Check for image marker
      const match = currentSectionText.match(/\[GÖRSEL:\s*(.*?)\s*\]/) || currentSectionText.match(/\[IMAGE:\s*(.*?)\s*\]/);
      if (match) {
        const imageDescription = match[1];
        
        // Remove marker from text
        currentSectionText = currentSectionText.replace(match[0], '');
        
        // Update text without marker and set image status
        setDocumentSections(prev => prev.map(s => 
          s.id === sectionId 
            ? { 
                ...s, 
                textContent: currentSectionText, 
                imageStatus: 'generating' 
              } 
            : s
        ));
          
        // Trigger image generation (non-blocking), passing the section's current settings
        const currentSection = documentSections.find(s => s.id === sectionId);
        const sectionSettings = currentSection ? {
          roomType: currentSection.roomType,
          roomStyle: currentSection.roomStyle,
          colorPalette: currentSection.colorPalette
        } : null;
        generateImageForSection(sectionId, imageDescription, true, sectionSettings);
      }
        
      // Mark section as completed
      setDocumentSections(prev => prev.map(s => 
        s.id === sectionId 
          ? { ...s, status: 'completed' } 
          : s
      ));
      
    } catch (error) {
      console.error(`Error regenerating section ${section.title}:`, error);
      
      // Update section with error
      setDocumentSections(prev => prev.map(s => 
        s.id === sectionId 
          ? { 
              ...s, 
              status: 'error', 
              error: `Section regeneration failed: ${error.message}` 
            } 
          : s
      ));
    }
  };
  
  // Regenerate a specific section's image
  const regenerateSectionImage = async (sectionId, customOptions = null) => {
    const section = documentSections.find(s => s.id === sectionId);
    if (!section) return;

    // --- Reroll Credit Check ---
    const rerollCost = 2; // Reduced from 5
    if (creditsLeft < rerollCost) {
      console.warn(`Not enough credits for image regeneration (Section ${sectionId}). Needed: ${rerollCost}, Available: ${creditsLeft}`);
      // Update the specific section's error state instead of global error
      setDocumentSections(prev => prev.map(s => 
        s.id === sectionId 
          ? { ...s, imageStatus: 'error', error: `Görsel yeniden oluşturma için yeterli kredi yok (${rerollCost} kredi gerekli).` }
          : s
      ));
      // Close the customization modal if it was open for this section
      if (imageCustomizationOpen && currentCustomizingSection?.id === sectionId) {
        setImageCustomizationOpen(false);
      }
      // *** Ensure we actually stop ***
      return; // Stop if not enough credits
    }

    // --- Consume Reroll Credits ---
    const consumed = await consumeAndAnimateCredits(rerollCost);
    if (!consumed) {
      console.error(`Credit consumption failed for image regeneration (Section ${sectionId}). Halting regeneration.`);
      // Error state should already be set by consumeAndAnimateCredits or the initial check
      setDocumentSections(prev => prev.map(s =>
        s.id === sectionId
          ? { ...s, imageStatus: 'error', error: globalError || `Görsel yeniden oluşturma kredileri düşülemedi.` }
          : s
      ));
      // Close the customization modal if it was open
      if (imageCustomizationOpen && currentCustomizingSection?.id === sectionId) {
        setImageCustomizationOpen(false);
      }
      return; // Stop if credit consumption fails
    }

    // --- Proceed with Regeneration (Credits consumed successfully) ---
    // Create a default image description if none exists
    const imageDescription = `${section.title} ile ilgili profesyonel bir mimari görselleştirme`;

    // Update section status
    setDocumentSections(prev => prev.map(s => 
      s.id === sectionId 
        ? { ...s, imageStatus: 'generating', error: null } 
        : s
    ));
    
    // Generate new image. Use custom options if provided, otherwise use section's current settings.
    let optionsToUse = customOptions;
    if (!optionsToUse) {
      const currentSection = documentSections.find(s => s.id === sectionId);
      if (currentSection) {
        optionsToUse = {
          roomType: currentSection.roomType,
          roomStyle: currentSection.roomStyle,
          colorPalette: currentSection.colorPalette
        };
      }
    }
    generateImageForSection(sectionId, imageDescription, true, optionsToUse);
  };
  
  // Open image customization modal
  const openImageCustomization = (sectionId) => {
    const section = documentSections.find(s => s.id === sectionId);
    if (!section) return;
    
    // Initialize the customization options with the section's current settings
    if (section.roomType) setSelectedRoomType(section.roomType);
    if (section.roomStyle) setSelectedRoomStyle(section.roomStyle);
    if (section.colorPalette) setSelectedColorPalette(section.colorPalette);
    
    setCurrentCustomizingSection(section);
    setImageCustomizationOpen(true);
  };
  
  // Apply custom image options and regenerate
  const applyCustomImageOptions = () => {
    if (!currentCustomizingSection) return;
    
    const customOptions = {
      roomType: selectedRoomType,
      roomStyle: selectedRoomStyle,
      colorPalette: selectedColorPalette
    };
    
    // Close modal
    const sectionIdToScrollTo = currentCustomizingSection.id; // Store the ID before closing modal

    // Close modal
    setImageCustomizationOpen(false);
    
    // Regenerate with custom options
    regenerateSectionImage(sectionIdToScrollTo, customOptions);

    // Scroll the relevant section into view after a short delay
    // This allows the modal to close and the regeneration state to update
    setTimeout(() => {
      const sectionElement = document.getElementById(sectionIdToScrollTo);
      if (sectionElement) {
        sectionElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' // Scrolls the element to the center of the view
        });
      }
    }, 150); // Small delay (adjust if needed)
  };

  // Continue document generation after plan approval
  const continueDocumentGeneration = async (approvedSections) => {
    // --- Phase 2: Calculate TOTAL Generation Cost (Text + Images) ---
    const textGenerationCostPerSection = 2; // Reduced from 5
    const imageGenerationCostPerSection = 2; // Reduced from 5, assuming 1 image per section initially
    const numSections = approvedSections.length;

    const totalTextCost = numSections * textGenerationCostPerSection;
    const totalImageCost = numSections * imageGenerationCostPerSection;
    const totalCombinedCost = totalTextCost + totalImageCost;

    console.log(`Estimated cost: ${numSections} sections * (${textGenerationCostPerSection} text + ${imageGenerationCostPerSection} image) = ${totalCombinedCost} credits.`);

    // --- Check Total Credits ---
    if (creditsLeft < totalCombinedCost) {
      setShowCreditWarning(true);
      setGlobalError(`Belge oluşturma için en az ${totalCombinedCost} krediye ihtiyacınız var (Metin: ${totalTextCost}, Görseller: ${totalImageCost}). Mevcut: ${creditsLeft}`);
      setShowPlanApproval(true); // Keep the plan approval dialog open
      return; // Stop before consuming credits or starting generation
    }

    // --- Consume TOTAL Credits using helper ---
    const consumed = await consumeAndAnimateCredits(totalCombinedCost);
    // *** Explicitly check if consumption failed and stop ***
    if (!consumed) {
      console.error(`Credit consumption failed for total cost (${totalCombinedCost}). Halting document generation.`);
      // Error state should already be set by consumeAndAnimateCredits or the initial check
      setGlobalError(globalError || `Toplam ${totalCombinedCost} kredi düşülemedi.`);
      setShowPlanApproval(true); // Keep plan approval open
      return; // Stop generation
    }

    // Reset states and start generation (Credits are now pre-consumed)
    setShowPlanApproval(false);
    setIsGenerating(true);
    setProgress(40);
    
    // Initialize document sections from approved titles with their settings
    const initialSections = approvedSections.map((title, index) => ({
      id: `sec-${index}`,
      title,
      status: 'pending',
      textContent: '',
      imageStatus: 'idle',
      imageUrl: null,
      error: null,
      roomType: plannedSectionSettings[index]?.roomType || selectedRoomType,
      roomStyle: plannedSectionSettings[index]?.roomStyle || selectedRoomStyle,
      colorPalette: plannedSectionSettings[index]?.colorPalette || selectedColorPalette
    }));
    
    setDocumentSections(initialSections);
    
    // --- Accumulate previous content for context ---
    let previousSectionsContent = ""; 
    
    // Generate sections sequentially
    for (let i = 0; i < initialSections.length; i++) {
      const sectionId = `sec-${i}`;
      const sectionTitle = initialSections[i].title;
      
      setCurrentStep(`Bölüm Oluşturuluyor: ${sectionTitle}`);
      setProgress(40 + Math.floor((i / initialSections.length) * 50));
      
      // Update section status
      setDocumentSections(prev => prev.map(s => 
        s.id === sectionId 
          ? { ...s, status: 'text_generating' } 
          : s
      ));
      
      try {
        // Construct text prompt with flow and conciseness instructions
        const totalSections = initialSections.length;
        const currentSectionNumber = i + 1;
        let sectionRole = "orta bölüm";
        if (currentSectionNumber === 1 && totalSections > 1) sectionRole = "giriş bölümü";
        if (currentSectionNumber === totalSections && totalSections > 1) sectionRole = "sonuç bölümü";

        const textPrompt = `"${sectionTitle}" başlıklı mimari bir makale için ${totalSections} bölümlük bir yazının ${currentSectionNumber}. bölümünü (${sectionRole}) yazın. Bu bölümü ilgi çekici ve sohbet tarzında oluşturun.

        ${analysisSummary ? `Görsel Analizi Bağlamı: ${analysisSummary}` : ''}

        Kullanıcı İsteği: ${userRequest}

        ${previousSectionsContent ? `Önceki Bölümlerin İçeriği:\n${previousSectionsContent}\n---\n` : ''}

        Önemli Talimatlar:
        1. **Akış ve Bağlam:** Bu bölümü, önceki içeriğin doğal bir devamı olarak yazın. Tekrarlayan giriş/sonuç ifadelerinden ("Sonuç olarak", "Özetle" vb.) kaçının, ancak bu bölüm **sonuç bölümü** ise uygun bir kapanış yapın.
        2. **Ton:** Profesyonel bir mimarın okuyucuyla konuştuğu gibi doğal, samimi bir ton kullanın.
        3. **Görsel İşareti:** Metinde uygun bir yere, görsel yerleştirileceğini belirtmek için [GÖRSEL: bu bölümü zenginleştirecek bir görselin kısa açıklaması] işaretini ekleyin.
        4. **Kısalık ve Biçim:** İçeriği özlü tutun (yaklaşık 150-200 kelime) ve 2-3 paragrafı geçmemeye çalışın. Vurgu için markdown kullanın.
        5. **Teknik Dil:** Aşırı teknik jargondan kaçının.
        6. **Kişisel Dokunuş:** "Genellikle şunu buluyoruz" gibi ifadelerle metni daha insancıl hale getirin.
        7. **Dil:** Metni Türkçe olarak yazın.`;

        let currentSectionText = '';
        
        try {
          let streamSuccess = false;
          
          try {
            // First try streaming approach
            console.log(`Starting streaming for section ${sectionTitle}`);
            const result = await textModel.generateContentStream({
              contents: [{ role: "user", parts: [{ text: textPrompt }] }],
              generationConfig: {
                temperature: 0.7,
                topK: 40,
                topP: 0.95,
                maxOutputTokens: 4096,
              },
            });
            
            // Process the stream with controlled pacing
            const stream = result.stream;
            if (!stream) throw new Error("Stream not available");
            
            for await (const chunk of stream) {
              const chunkText = chunk.text();
              currentSectionText += chunkText;
              
              // Update UI with streaming text
              setDocumentSections(prev => prev.map(s => 
                s.id === sectionId 
                  ? { ...s, textContent: currentSectionText } 
                  : s
              ));
              
              // Longer delay to make the streaming more visible and readable
              // This slows down the text appearance to feel more natural
              await new Promise(res => setTimeout(res, 50));
            }
            
            streamSuccess = true;
            console.log(`Streaming completed successfully for section ${sectionTitle}`);
          } catch (streamError) {
            console.warn(`Streaming failed for section ${sectionTitle}:`, streamError);
            streamSuccess = false;
          }
          
          // If streaming failed or produced no content, fall back to non-streaming
          if (!streamSuccess || !currentSectionText.trim()) {
            console.log(`Falling back to non-streaming for section ${sectionTitle}`);
            // Show user that we're switching to non-streaming mode
            setDocumentSections(prev => prev.map(s => 
              s.id === sectionId 
                ? { 
                    ...s, 
                    textContent: "Streaming failed. Switching to standard generation mode...",
                    status: 'text_generating'
                  } 
                : s
            ));
            
            // Wait a moment to show the message
            await new Promise(res => setTimeout(res, 1000));
            
            try {
              // Fallback to non-streaming approach with a more robust error handling
              const result = await textModel.generateContent({
                contents: [{ role: "user", parts: [{ text: textPrompt }] }],
                generationConfig: {
                  temperature: 0.7,
                  topK: 40,
                  topP: 0.95,
                  maxOutputTokens: 4096,
                },
              });
              
              if (result && result.response) {
                currentSectionText = result.response.text();
                console.log(`Non-streaming generation successful for section ${sectionTitle}`);
                
                // Simulate streaming for better UX even in fallback mode
                let displayedText = "";
                const words = currentSectionText.split(" ");
                
                for (let i = 0; i < words.length; i += 3) { // Show 3 words at a time
                  const chunk = words.slice(i, i + 3).join(" ") + " ";
                  displayedText += chunk;
                  
                  // Update UI with the simulated streaming text
                  setDocumentSections(prev => prev.map(s => 
                    s.id === sectionId 
                      ? { ...s, textContent: displayedText } 
                      : s
                  ));
                  
                  // Delay between chunks to simulate typing
                  await new Promise(res => setTimeout(res, 100));
                }
                
                // Final update with complete text
                setDocumentSections(prev => prev.map(s => 
                  s.id === sectionId 
                    ? { ...s, textContent: currentSectionText } 
                    : s
                ));
              } else {
                throw new Error("Empty response from non-streaming generation");
              }
            } catch (nonStreamingError) {
              console.error(`Non-streaming generation also failed for section ${sectionTitle}:`, nonStreamingError);
              
              // Last resort: Generate a simple placeholder text
              currentSectionText = `Bu bölüm ${sectionTitle} konusunu ele alacaktır. 
          
              İçerik genellikle mimari öğeler, tasarım düşünceleri ve projenin bu yönüyle ilgili teknik özellikler hakkında ayrıntılı bilgiler içerecektir.
          
              [GÖRSEL: ${sectionTitle} ile ilgili profesyonel bir mimari görselleştirme]
          
              Daha spesifik içeriğe ihtiyacınız varsa lütfen bu bölümü yeniden oluşturun.`;
              
              setDocumentSections(prev => prev.map(s => 
                s.id === sectionId 
                  ? { 
                      ...s, 
                      textContent: currentSectionText,
                      error: `Generation failed: ${nonStreamingError.message}. Using placeholder content.`
                    } 
                  : s
              ));
            }
          }
        } catch (error) {
          console.error(`Unexpected error in text generation for section ${sectionTitle}:`, error);
          
          // Ensure we have some content even if everything fails
          if (!currentSectionText.trim()) {
            currentSectionText = `Bu bölüm ${sectionTitle} konusunu ele alacaktır. 
            
            İçerik genellikle mimari öğeler, tasarım düşünceleri ve projenin bu yönüyle ilgili teknik özellikler hakkında ayrıntılı bilgiler içerecektir.
            
            [GÖRSEL: ${sectionTitle} ile ilgili profesyonel bir mimari görselleştirme]
            
            Daha spesifik içeriğe ihtiyacınız varsa lütfen bu bölümü yeniden oluşturun.`;
            
            setDocumentSections(prev => prev.map(s => 
              s.id === sectionId 
                ? { 
                    ...s, 
                    textContent: currentSectionText,
                    error: `Generation failed: ${error.message}. Using placeholder content.`
                  } 
                : s
            ));
          }
        }
        
        // --- Append generated text to context for next iteration ---
        if (currentSectionText.trim()) {
          previousSectionsContent += `\n\n## ${sectionTitle}\n\n${currentSectionText.trim()}`; 
        }
        
        // Check for image marker
        const match = currentSectionText.match(/\[GÖRSEL:\s*(.*?)\s*\]/) || currentSectionText.match(/\[IMAGE:\s*(.*?)\s*\]/);
        if (match) {
          const imageDescription = match[1];
          
          // Remove marker from text
          currentSectionText = currentSectionText.replace(match[0], '');
          
          // Update text without marker and set image status
          setDocumentSections(prev => prev.map(s => 
            s.id === sectionId 
              ? { 
                  ...s, 
                  textContent: currentSectionText, 
                  imageStatus: 'generating' 
                } 
              : s
          ));
          
          // Trigger image generation (non-blocking), passing the section's settings directly from initialSections
          // Use initialSections[i] as documentSections state might be stale inside the loop
          const sectionSettings = initialSections[i] ? {
            roomType: initialSections[i].roomType,
            roomStyle: initialSections[i].roomStyle,
            colorPalette: initialSections[i].colorPalette
          } : null; // Fallback if index is somehow out of bounds
          
          generateImageForSection(sectionId, imageDescription, false, sectionSettings);
        }
        
        // Mark section as completed
        setDocumentSections(prev => prev.map(s => 
          s.id === sectionId 
            ? { ...s, status: 'completed' } 
            : s
        ));
        
      } catch (error) {
        console.error(`Error generating section ${sectionTitle}:`, error);
        
        // Update section with error
        setDocumentSections(prev => prev.map(s => 
          s.id === sectionId 
            ? { 
                ...s, 
                status: 'error', 
                error: `Section generation failed: ${error.message}` 
              } 
            : s
        ));
      }
      
      // Small delay between sections
      await new Promise(res => setTimeout(res, 200));
    }
    
    // Upload generated images to Supabase storage
    setCurrentStep("Uploading images to storage...");
    setProgress(95);
    
    try {
      // Find all sections with images
      const sectionsWithImages = documentSections.filter(
        section => section.imageStatus === 'completed' && section.imageUrl
      );
      
      // Upload each image to Supabase
      for (const section of sectionsWithImages) {
        // Skip if the URL is already from Supabase
        if (section.imageUrl.includes('supabase.co/storage')) continue;
        
        try {
          // Convert blob URL to actual blob
          const response = await fetch(section.imageUrl);
          const blob = await response.blob();
          
          // Create a file from the blob
          const fileName = `document-${Date.now()}-${section.id}.png`;
          const file = new File([blob], fileName, { type: 'image/png' });
          
          // Upload to Supabase
          const filePath = `${session?.user?.id || 'anonymous'}/${fileName}`;
          
          const { data, error } = await supabase.storage
            .from('generated-images')
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: true // Changed to true to overwrite existing files
            });
            
          if (error) {
            console.error(`Error uploading image for section ${section.id}:`, error);
            continue; // Continue with next image even if this one fails
          }
          
          // Get the public URL
          const { data: { publicUrl } } = supabase.storage
            .from('generated-images')
            .getPublicUrl(filePath);
            
          if (publicUrl) {
            console.log(`Successfully uploaded image for section ${section.id} to: ${publicUrl}`);
            
            // Update the section with the new URL
            setDocumentSections(prev => prev.map(s => 
              s.id === section.id 
                ? { ...s, imageUrl: publicUrl, supabaseStoragePath: filePath } 
                : s
            ));
            
            // Don't revoke the blob URL yet - we'll do it after HTML generation
          } else {
            console.error(`Failed to get public URL for section ${section.id}`);
          }
        } catch (sectionError) {
          console.error(`Error processing image for section ${section.id}:`, sectionError);
        }
      }
    } catch (uploadError) {
      console.error('Error uploading images to Supabase:', uploadError);
      // Don't throw here, we'll continue with local blob URLs if upload fails
    }
    
    // Finish
    setCurrentStep("Document generation complete!");
    setProgress(100);
    
    // Scroll to results
    if (resultContainerRef.current) {
      resultContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    
    // Reset generation state
    setIsGenerating(false);
    setShowTotalConsumedMessage(true); // Show the total consumed message
  };

  // Main document generation function
  const handleGenerateDocument = async () => {
    // --- Phase 1: Planning Credit Check ---
    const planningCost = 2; // Reduced from 5
    if (creditsLeft < planningCost) {
      setShowCreditWarning(true); // Use a more specific warning later if needed
      setGlobalError(`Planlama aşaması için en az ${planningCost} krediye ihtiyacınız var.`);
      return;
    }
    
    // Initialize generation
    setIsGenerating(true);
    setGlobalError(null);
    setDocumentSections([]);
    setCurrentStep("Preparing...");
    setProgress(5);
    setTotalCreditsConsumedThisSession(0); // Reset total consumed for new session
    setShowTotalConsumedMessage(false); // Hide previous total message
    
    try {
      // --- Consume Planning Credits using helper ---
      const consumed = await consumeAndAnimateCredits(planningCost);
      // *** Explicitly check if consumption failed and stop ***
      if (!consumed) {
        console.error("Credit consumption failed for planning phase. Halting document generation.");
        // Error state should already be set by consumeAndAnimateCredits or the initial check
        setGlobalError(globalError || `Planlama kredileri düşülemedi.`);
        setIsGenerating(false); // Stop generation if credit consumption fails
        return; // *** Ensure we stop ***
      }

      // Convert source images to base64
      setCurrentStep("Converting images...");
      setProgress(10);
      
      let imageContentParts = [];
      if (sourceImages.length > 0) {
        try {
          const base64Images = await Promise.all(sourceImages.map(fileToBase64));
          
          // Prepare image content parts for Gemini
          imageContentParts = base64Images.map(base64 => {
            const mimeType = base64.split(';')[0].split(':')[1];
            const base64Data = base64.split(',')[1];
            return {
              inlineData: {
                mimeType,
                data: base64Data
              }
            };
          });
        } catch (error) {
          throw new Error(`Error converting images: ${error.message}`);
        }
      }
      
      // Initial analysis of images (if any)
      let analysisText = '';
      if (imageContentParts.length > 0) {
        setCurrentStep("Resimler analiz ediliyor...");
        setProgress(20);
        
        try {
          // Construct multimodal prompt
          const analysisPrompt = `Bu mimari görselleri detaylı olarak analiz edin. 
          Temel mimari öğeleri, stili, malzemeleri, mekansal organizasyonu ve dikkat çekici tasarım özelliklerini açıklayın.
          Birden fazla görsel varsa, aralarındaki ilişkileri belirtin.
          Kullanıcı isteği bağlamı: ${userRequest}`;
          
          const contentParts = [
            { text: analysisPrompt },
            ...imageContentParts
          ];
          
          // Generate analysis
          const analysisResult = await textModel.generateContent({
            contents: [{ role: "user", parts: contentParts }],
            generationConfig: {
              temperature: 0.2,
              topK: 1,
              topP: 0.95,
              maxOutputTokens: 2048,
            },
          });
          
          analysisText = analysisResult.response.text();
          setAnalysisSummary(analysisText);
        } catch (error) {
          console.error('Image analysis error:', error);
          // Instead of throwing, we'll continue with a default analysis
          analysisText = `Detaylı görsel analizi yapılamadı. Kullanıcı isteğine dayanarak belge oluşturmaya devam ediliyor: ${userRequest}`;
          setAnalysisSummary(analysisText);
          // Show a warning to the user
          setGlobalError(`Warning: Image analysis encountered an issue, but we'll continue with document generation.`);
          // Wait 3 seconds then clear the error
          setTimeout(() => setGlobalError(null), 3000);
        }
      }
      
      // Plan document structure
      setCurrentStep("Döküman şeması şekillendiriliyor...");
      setProgress(30);
      
      try {
        
        // Construct planning prompt with instructions for settings
        const planningPrompt = `Aşağıdaki bilgilere dayanarak, 3-4 bölümlü mimari bir makale için blog tarzı bir taslak oluşturun. Her bölüm için, analiz ve kullanıcı isteğine en uygun oda tipi, stil ve renk paletini belirleyin.

        ${analysisText ? `Görsel Analizi: ${analysisText}` : ''}
        
        Kullanıcı İsteği: ${userRequest}

        Geçerli Seçenekler:
        - Oda Tipleri (roomType): ${roomTypes.map(t => t.value).join(', ')}
        - Oda Stilleri (roomStyle): ${roomStyles.map(s => s.value).join(', ')}
        - Renk Paletleri (colorPalette): ${colorPalettes.map(p => p.value).join(', ')}

        SADECE bu tam formatta bir JSON dizisi olarak bölüm başlıklarını ve ayarlarını döndürün:
        [
          { "title": "Bölüm Başlığı 1", "roomType": "uygun_oda_tipi", "roomStyle": "uygun_stil", "colorPalette": "uygun_palet" },
          { "title": "Bölüm Başlığı 2", "roomType": "uygun_oda_tipi", "roomStyle": "uygun_stil", "colorPalette": "uygun_palet" },
          ...
        ]
        
        Bölümleri sohbet tarzında, ilgi çekici ve bir blog yazısı gibi doğal akışlı yapın. Mimarlık ve tasarımla ilgilenen okuyucuların ilgisini çekecek başlıklar kullanın. Ayarları seçerken bölüm başlığı ve genel bağlamı göz önünde bulundurun. YALNIZCA sağlanan geçerli seçeneklerden değerler kullanın.`;
        
        // Generate plan
        const planningResult = await textModel.generateContent({
          contents: [{ role: "user", parts: [{ text: planningPrompt }] }],
          generationConfig: {
            temperature: 0.2,
            topK: 1,
            topP: 0.95,
            maxOutputTokens: 1024,
          },
        });
        
        const planText = planningResult.response.text();
        console.log("Planning response:", planText);

        let parsedPlan = [];
        let newSectionTitles = [];
        let newSectionSettings = [];
        let parsingError = null;

        try {
          // Extract JSON array if it's embedded in other text
          const jsonMatch = planText.match(/\[\s*\{[\s\S]*?\}\s*\]/s); // More robust regex for array of objects
          if (jsonMatch) {
            try {
              parsedPlan = JSON.parse(jsonMatch[0]);
            } catch (jsonError) {
              console.error("Error parsing JSON match:", jsonError);
              // Try cleaning the JSON string (basic cleaning)
              const cleanedJson = jsonMatch[0]
                .replace(/\\n/g, "")
                .replace(/\\"/g, '"')
                .replace(/,\s*\]/g, ']') // Remove trailing commas before closing bracket
                .replace(/,\s*}/g, '}'); // Remove trailing commas before closing brace
              try {
                parsedPlan = JSON.parse(cleanedJson);
              } catch (cleanError) {
                console.error("Error parsing cleaned JSON:", cleanError);
                parsingError = cleanError; // Store error for fallback
              }
            }
          } else {
             parsingError = new Error("No valid JSON array of objects found in the response.");
          }

          if (!Array.isArray(parsedPlan) || parsedPlan.length === 0 || parsedPlan.some(item => typeof item !== 'object' || !item.title)) {
             parsingError = parsingError || new Error("Parsed data is not a valid array of section objects with titles.");
          }

          // If primary parsing failed, throw the error to trigger fallback
          if (parsingError) {
            throw parsingError;
          }

          // Validate and extract data if parsing was successful
          const validRoomTypes = new Set(roomTypes.map(t => t.value));
          const validRoomStyles = new Set(roomStyles.map(s => s.value));
          const validColorPalettes = new Set(colorPalettes.map(p => p.value));

          parsedPlan.forEach(item => {
            newSectionTitles.push(item.title || `Bölüm ${newSectionTitles.length + 1}`); // Fallback title

            const settings = {
              roomType: validRoomTypes.has(item.roomType) ? item.roomType : selectedRoomType,
              roomStyle: validRoomStyles.has(item.roomStyle) ? item.roomStyle : selectedRoomStyle,
              colorPalette: validColorPalettes.has(item.colorPalette) ? item.colorPalette : selectedColorPalette,
            };
            newSectionSettings.push(settings);
          });

          console.log("Successfully parsed plan with settings:", { titles: newSectionTitles, settings: newSectionSettings });

        } catch (error) {
          console.error("Error parsing section plan with settings:", error);
          console.log("Raw planning text:", planText);
          parsingError = error; // Ensure error is captured

          // --- Fallback Logic ---
          // Try to extract titles manually if JSON parsing fails completely
          if (planText.includes('"') && planText.includes('\n')) {
            const lines = planText.split('\n');
            const titleLines = lines.filter(line =>
              line.includes('"') ||
              line.includes('Section') ||
              /^\d+\./.test(line.trim()) ||
              line.trim().startsWith('-') // Handle bullet points
            );

            if (titleLines.length > 0) {
              newSectionTitles = titleLines.map(line => {
                const titleMatch = line.match(/"([^"]+)"|(\d+\.\s*[^,]+)|Section\s*\d+:\s*([^,]+)|-\s*(.*)/);
                return titleMatch ? (titleMatch[1] || titleMatch[2] || titleMatch[3] || titleMatch[4]).trim() : line.trim();
              }).filter(title => title); // Filter out empty titles
              console.log("Manually extracted fallback titles:", newSectionTitles);
            }
          }

          // If manual extraction also fails or yields no titles, use default sections
          if (newSectionTitles.length === 0) {
            console.log("Using default section titles as fallback.");
            newSectionTitles = [
              "Projeye Giriş",
              "Mimari Analiz",
              "Tasarım Konsepti ve Vizyon",
              "Sonuç ve Gelecek Yönelimler"
            ];
          }

          // Use default settings for all sections in fallback scenarios
          newSectionSettings = newSectionTitles.map(() => ({
            roomType: selectedRoomType,
            roomStyle: selectedRoomStyle,
            colorPalette: selectedColorPalette,
          }));
        }

        // Show plan approval dialog with potentially parsed/fallback titles and settings
        setPlannedSections(newSectionTitles);
        setPlannedSectionSettings(newSectionSettings); // Set the settings state
        setShowPlanApproval(true);
        setIsGenerating(false);

        // Show a warning if fallback logic was used
        if (parsingError) {
          setGlobalError(`Planlama yanıtı ayrıştırılamadı, varsayılan veya manuel olarak çıkarılan başlıklar/ayarlar kullanılıyor.`);
          setTimeout(() => setGlobalError(null), 4000); // Show warning for 4 seconds
        }

        return; // Stop here and wait for user approval

      } catch (error) {
         // This catch block handles errors *before* parsing (e.g., API call failure)
        console.error('Document planning API call error:', error);
        // Create default sections and settings as a last resort
        const defaultTitles = [
          "Projeye Giriş",
          "Mimari Analiz",
          "Tasarım Konsepti ve Vizyon",
          "Teknik Uygulama",
          "Sonuç ve Gelecek Yönelimler"
        ];
         const defaultSettings = defaultTitles.map(() => ({
            roomType: selectedRoomType,
            roomStyle: selectedRoomStyle,
            colorPalette: selectedColorPalette,
          }));

        setPlannedSections(defaultTitles);
        setPlannedSectionSettings(defaultSettings);
        setShowPlanApproval(true);
        setIsGenerating(false);
        setGlobalError(`Planlama sırasında bir hata oluştu, varsayılan bölümler oluşturuldu.`);
        setTimeout(() => setGlobalError(null), 3000);
        return; // Stop here and wait for user approval
      }

      // This part of the code is now unreachable because we return earlier
      // It should be removed or refactored if the logic changes.
      /*
      for (let i = 0; i < documentSections.length; i++) {
        const sectionId = `sec-${i}`;
        // ... rest of the loop ...
      }
      */
    } catch (error) {
      // This catch handles errors in the initial setup before the planning API call
      console.error('Document generation failed (initial setup):', error);
      setGlobalError(`Belge oluşturma başarısız oldu: ${error.message}`);
    } finally {
      // Ensure isGenerating is always reset if something unexpected happens
      // Note: It's already set to false before returning in the planning logic
      if (isGenerating) {
         setIsGenerating(false);
      }
    }
  };

  // Download document as HTML
  const handleDownloadDocument = async () => {
    if (documentSections.length === 0) return;
    
    try {
      // Track which images are from blob URLs vs Supabase
      const blobUrls = [];
      const imageMap = new Map(); // Map to store base64 versions of images
      
      // Create HTML content
      let htmlContent = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Mimari Blog</title>
        <style>
          body {
            font-family: 'Georgia', serif;
            line-height: 1.8;
            color: #333;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
            background-color: #fff;
          }
          h1 {
            font-family: 'Helvetica Neue', Arial, sans-serif;
            color: #333;
            font-size: 2.5rem;
            margin-bottom: 1.5rem;
            text-align: center;
          }
          h2 {
            font-family: 'Helvetica Neue', Arial, sans-serif;
            color: #444;
            font-size: 1.8rem;
            margin-top: 2.5rem;
            margin-bottom: 1rem;
            border-bottom: 1px solid #eee;
            padding-bottom: 0.5rem;
          }
          p {
            margin-bottom: 1.5rem;
            font-size: 1.1rem;
          }
          img {
            max-width: 100%;
            height: auto;
            border-radius: 8px;
            margin: 25px auto;
            display: block;
            box-shadow: 0 5px 15px rgba(0,0,0,0.1);
          }
          .section {
            margin-bottom: 3rem;
          }
          .date {
            color: #888;
            font-style: italic;
            text-align: center;
            margin-bottom: 2rem;
          }
          .header-logo {
            position: absolute;
            top: 15px;
            right: 20px;
            max-height: 50px; /* Adjust max height as needed */
            max-width: 150px; /* Adjust max width as needed */
          }
          blockquote {
            border-left: 4px solid #ddd;
            padding-left: 1rem;
            font-style: italic;
            color: #555;
          }
          strong {
            color: #222;
          }
        </style>
      </head>
      <body>
        <h1>${documentTitle}</h1> 
        <div class="date">${new Date().toLocaleDateString('tr-TR', {year: 'numeric', month: 'long', day: 'numeric'})}</div>
      `;

      // --- Add Logo to HTML Header if selected ---
      let logoBase64 = null;
      if (includeLogoInExport && selectedLogoUrl) {
        try {
          const response = await fetch(selectedLogoUrl);
          if (!response.ok) throw new Error('Logo fetch failed');
          const blob = await response.blob();
          logoBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          htmlContent += `<img src="${logoBase64}" alt="Logo" class="header-logo">`;
        } catch (logoError) {
          console.error("Error fetching or embedding logo for HTML:", logoError);
          // Optionally add a placeholder or error message in HTML
        }
      }
      
      // Add each section
      documentSections.forEach(section => {
        htmlContent += `
        <div class="section">
          <h2>${section.title}</h2>
          <div>${section.textContent.replace(/\n/g, '<br>')}</div>
          ${section.imageUrl ? `<img src="${section.imageUrl}" alt="${section.title} visualization" id="img-${section.id}">` : ''}
        </div>
        ${section.imageUrl && !section.imageUrl.includes('supabase.co/storage') ? blobUrls.push(section.imageUrl) && '' : ''}
        `;
      });
      
      htmlContent += `
      </body>
      </html>
      `;
      
      // Create a complete HTML document with embedded images
      const tempDoc = document.implementation.createHTMLDocument("Mimari Blog");
      
      // Set up the HTML structure
      tempDoc.documentElement.innerHTML = `
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Mimari Blog</title>
  <style>
    body {
      font-family: 'Georgia', serif;
      line-height: 1.8;
      color: #333;
      max-width: 800px;
      margin: 0 auto;
      padding: 20px;
      background-color: #fff;
    }
    h1 {
      font-family: 'Helvetica Neue', Arial, sans-serif;
      color: #333;
      font-size: 2.5rem;
      margin-bottom: 1.5rem;
      text-align: center;
    }
    h2 {
      font-family: 'Helvetica Neue', Arial, sans-serif;
      color: #444;
      font-size: 1.8rem;
      margin-top: 2.5rem;
      margin-bottom: 1rem;
      border-bottom: 1px solid #eee;
      padding-bottom: 0.5rem;
    }
    p {
      margin-bottom: 1.5rem;
      font-size: 1.1rem;
    }
    img {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
      margin: 25px auto;
      display: block;
      box-shadow: 0 5px 15px rgba(0,0,0,0.1);
    }
    .section {
      margin-bottom: 3rem;
    }
    .date {
      color: #888;
      font-style: italic;
      text-align: center;
      margin-bottom: 2rem;
    }
    blockquote {
      border-left: 4px solid #ddd;
      padding-left: 1rem;
      font-style: italic;
      color: #555;
    }
    strong {
      color: #222;
    }
  </style>
</head>
<body>
  <h1>${documentTitle}</h1>
  <div class="date">${new Date().toLocaleDateString('tr-TR', {year: 'numeric', month: 'long', day: 'numeric'})}</div>
  ${logoBase64 ? `<img src="${logoBase64}" alt="Logo" class="header-logo">` : ''}
</body>
</html>`;
      
      // Add each section to the body
      const bodyElement = tempDoc.body;
      
      // First, cache all images before processing sections
      const imageCache = new Map();
      
      // Pre-fetch all images
      const prefetchPromises = documentSections
        .filter(section => section.imageUrl)
        .map(async (section) => {
          try {
            if (!section.imageUrl.includes('supabase.co/storage')) {
              // Create a copy of the blob to ensure it doesn't get revoked
              const response = await fetch(section.imageUrl);
              if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);
              
              const blob = await response.blob();
              
              // Convert to base64
              const base64 = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
              });
              
              // Store in cache
              imageCache.set(section.id, base64);
            } else {
              // For Supabase URLs, just store the URL
              imageCache.set(section.id, section.imageUrl);
            }
          } catch (error) {
            console.error(`Error prefetching image for section ${section.id}:`, error);
            // Store error in cache
            imageCache.set(section.id, null);
          }
        });
      
      // Wait for all prefetching to complete
      await Promise.all(prefetchPromises);
      
      // Now process sections with cached images
      const processPromises = documentSections.map(async (section, index) => {
        // Create section container
        const sectionDiv = tempDoc.createElement('div');
        sectionDiv.className = 'section';
        
        // Add title
        const titleElement = tempDoc.createElement('h2');
        titleElement.textContent = section.title;
        sectionDiv.appendChild(titleElement);
        
        // Add content
        const contentDiv = tempDoc.createElement('div');
        contentDiv.innerHTML = section.textContent.replace(/\n/g, '<br>');
        sectionDiv.appendChild(contentDiv);
        
        // Add image if available
        if (section.imageUrl) {
          // Create image element
          const imgElement = tempDoc.createElement('img');
          imgElement.alt = `${section.title} visualization`;
          
          // Get image from cache
          const cachedImage = imageCache.get(section.id);
          
          if (cachedImage) {
            imgElement.src = cachedImage;
            sectionDiv.appendChild(imgElement);
          } else {
            // Add a placeholder if image couldn't be cached
            const errorMsg = tempDoc.createElement('p');
            errorMsg.textContent = 'Image could not be loaded';
            errorMsg.style.color = 'red';
            sectionDiv.appendChild(errorMsg);
          }
        }
        
        // Add the section to the body
        bodyElement.appendChild(sectionDiv);
      });
      
      // Wait for all sections to be processed
      await Promise.all(processPromises);
      
      // Create blob and download
      const blob = new Blob([tempDoc.documentElement.outerHTML], { type: 'text/html' });
      
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = 'architectural-document.html';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        // Now it's safe to revoke the blob URLs since they've been embedded in the HTML
        blobUrls.forEach(blobUrl => {
          try {
            URL.revokeObjectURL(blobUrl);
          } catch (e) {
            console.error('Error revoking URL:', blobUrl, e);
          }
        });
      }, 100);
      
    } catch (error) {
      console.error('Error downloading document:', error);
      setGlobalError(`Download failed: ${error.message}`);
    } // Added closing brace for catch block
  }; // Added closing brace for handleDownloadDocument function

  // Download document as PDF
  const handleDownloadPdf = async () => {
    if (documentSections.length === 0) return;
    setIsDownloadingPdf(true);
    setGlobalError(null);
    let fontLoaded = false;
    const pdf = new jsPDF('p', 'pt', 'a4'); // Initialize PDF object first

    try {
      // --- Load Custom Fonts with Turkish support ---
      let fontsLoaded = 0;
      const fontVariants = [
        { name: 'NotoSans', style: 'normal', file: 'NotoSans-Regular.ttf' },
        { name: 'NotoSans', style: 'bold', file: 'NotoSans-Bold.ttf' },
        { name: 'NotoSans', style: 'italic', file: 'NotoSans-Italic.ttf' }
      ];

      try {
        // Load all font variants
        for (const variant of fontVariants) {
          const fontUrl = `/fonts/${variant.file}`;
          const response = await fetch(fontUrl);
          if (!response.ok) {
            throw new Error(`Failed to fetch font: ${variant.file}`);
          }
          const fontBlob = await response.blob();
          const fontBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(fontBlob);
          });

          // Register font with jsPDF
          pdf.addFileToVFS(variant.file, fontBase64);
          pdf.addFont(variant.file, variant.name, variant.style);
          fontsLoaded++;
        }

        pdf.setFont('NotoSans'); // Set as default font
        fontLoaded = true;
        console.log(`${fontsLoaded} NotoSans font variants with Turkish support loaded successfully.`);
      } catch (fontError) {
        console.error("Failed to load or register custom font:", fontError);
        setGlobalError("Custom font could not be loaded for PDF export. Using default font.");
        // Continue with default font
      }

      // PDF object is already initialized above
      const pageHeight = pdf.internal.pageSize.getHeight();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 40; // Page margin in points
      const maxLineWidth = pageWidth - margin * 2;
      const IMAGE_SCALE_FACTOR = 0.6; // Scale images to 60%
      const NEW_PAGE_TOP_PADDING = 15; // Extra space at the top of new pages
      let currentY = margin; // Start position for content

      // --- Helper function to add text and handle page breaks ---
      const addText = (text, options, yPosition) => {
        const { fontSize = 10, fontStyle = 'normal', isTitle = false } = options;
        // Use NotoSans if loaded, otherwise fallback to helvetica
        const fontName = fontLoaded ? 'NotoSans' : 'helvetica';
        
        // Map font styles to available variants
        let styleToUse = fontStyle;
        if (fontName === 'NotoSans') {
          if (fontStyle === 'bolditalic') {
            styleToUse = 'bold'; // Fallback to bold if bolditalic not available
          } else if (fontStyle === 'italic') {
            styleToUse = 'italic';
          } else {
            styleToUse = fontStyle === 'bold' ? 'bold' : 'normal';
          }
        }
        
        pdf.setFont(fontName, styleToUse);
        pdf.setFontSize(fontSize);

        // Split text into lines that fit the page width
        const lines = pdf.splitTextToSize(text, maxLineWidth);

        lines.forEach(line => {
          if (yPosition + fontSize > pageHeight - margin) { // Check if new page is needed
            pdf.addPage();
            yPosition = margin + NEW_PAGE_TOP_PADDING; // Reset Y position for new page with padding
          }
          pdf.text(line, margin, yPosition);
          yPosition += fontSize * 1.2; // Move Y down for next line (1.2 line spacing)
        });
        return yPosition; // Return the updated Y position
      };

      // --- Helper function to add image and handle page breaks ---
      const addImage = async (imageData, yPosition) => {
        try {
          // imageData can be either a URL or a base64 string
          let base64Image;
          
          if (imageData.startsWith('data:')) {
            // Already base64
            base64Image = imageData;
          } else if (imageData.includes('supabase.co/storage')) {
            // Supabase URL - fetch and convert
            const response = await fetch(imageData);
            if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);
            const blob = await response.blob();
            
            base64Image = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
          } else {
            throw new Error('Unsupported image format');
          }

          const imgProps = pdf.getImageProperties(base64Image);
          const aspectRatio = imgProps.height / imgProps.width;

          // Calculate dimensions based on 60% of the available page width
          const targetWidth = maxLineWidth * IMAGE_SCALE_FACTOR;
          const targetHeight = targetWidth * aspectRatio;

          // Check if image fits on the current page
          if (yPosition + targetHeight > pageHeight - margin) {
            pdf.addPage();
            yPosition = margin + NEW_PAGE_TOP_PADDING; // Reset Y position for new page with padding
          }

          // Calculate the x-coordinate to center the image
          const xPosition = (pageWidth - targetWidth) / 2;

          // Add image using the calculated target dimensions and centered x-position
          pdf.addImage(base64Image, 'PNG', xPosition, yPosition, targetWidth, targetHeight);
          return yPosition + targetHeight + 20; // Return Y position after image + spacing

        } catch (error) {
          console.error("Error adding image to PDF:", error);
          // Add error text instead of image
          return addText(`[Error loading image: ${error.message}]`, { fontSize: 9, fontStyle: 'italic' }, yPosition);
        }
      };

      // --- Add Document Header ---
      pdf.setFillColor(74, 144, 226); // Blue background
      pdf.rect(0, 0, pageWidth, 80, 'F'); // Header background
      
      // Add title with white color using state
      pdf.setTextColor(255, 255, 255);
      currentY = addText(documentTitle, { 
        fontSize: 28, 
        fontStyle: 'bold',
        color: [255, 255, 255]
      }, 40);
      
      // Add date in top right corner
      const dateStr = new Date().toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      pdf.setFontSize(10); // Smaller font size for date
      pdf.setFont('NotoSans', 'italic');
      const dateWidth = pdf.getStringUnitWidth(dateStr) * 10; // Use correct font size
      const dateY = 35; // Position date slightly higher
      pdf.text(dateStr, pageWidth - margin - dateWidth, dateY, { color: [220, 220, 220] });

      // --- Add Logo to PDF Header if selected ---
      if (includeLogoInExport && selectedLogoUrl) {
        try {
          const response = await fetch(selectedLogoUrl);
          if (!response.ok) throw new Error('Logo fetch failed');
          const blob = await response.blob();
          const logoBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result); // Get base64 string
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          const logoProps = pdf.getImageProperties(logoBase64);
          const logoMaxHeight = 40; // Max height for logo in header
          const logoMaxWidth = 100; // Max width for logo
          let logoWidth = logoProps.width;
          let logoHeight = logoProps.height;
          const aspectRatio = logoWidth / logoHeight;

          if (logoHeight > logoMaxHeight) {
            logoHeight = logoMaxHeight;
            logoWidth = logoHeight * aspectRatio;
          }
          if (logoWidth > logoMaxWidth) {
            logoWidth = logoMaxWidth;
            logoHeight = logoWidth / aspectRatio;
          }

          const logoX = pageWidth - margin - logoWidth; // Position right aligned
          const logoY = dateY + 10; // Position below the date

          pdf.addImage(logoBase64, logoProps.fileType, logoX, logoY, logoWidth, logoHeight);

        } catch (logoError) {
          console.error("Error fetching or adding logo to PDF:", logoError);
          // Optionally add error text to PDF
          pdf.setTextColor(255, 0, 0);
          pdf.setFontSize(8);
          pdf.text("[Logo Error]", pageWidth - margin - 50, 65);
        }
      }

      // Reset text color and position, start content below header with padding
      pdf.setTextColor(0, 0, 0);
      currentY = 100 + NEW_PAGE_TOP_PADDING; 

      // First cache all images before processing sections
      const imageCache = new Map();
      
      // Pre-fetch all images and convert to base64
      await Promise.all(documentSections.map(async (section) => {
        if (section.imageUrl && section.imageStatus === 'completed') {
          try {
            // Skip if already cached or if it's a Supabase URL
            if (imageCache.has(section.id) || section.imageUrl.includes('supabase.co/storage')) {
              return;
            }

            // Fetch the image
            const response = await fetch(section.imageUrl);
            if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);
            
            const blob = await response.blob();
            
            // Convert to base64
            const base64 = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
            
            // Store in cache
            imageCache.set(section.id, base64);
          } catch (error) {
            console.error(`Error caching image for section ${section.id}:`, error);
            // Store error in cache
            imageCache.set(section.id, null);
          }
        }
      }));

      // --- Add Sections ---
      for (const [index, section] of documentSections.entries()) {
        // Start each section (except the first) on a new page
        if (index > 0) {
          pdf.addPage();
          currentY = margin + NEW_PAGE_TOP_PADDING; // Reset Y for the new page
        }
        
        // Add Section Title with accent color
        pdf.setTextColor(74, 144, 226); // Blue color for titles
        currentY = addText(section.title, {
          fontSize: 18, 
          fontStyle: 'bold',
          color: [74, 144, 226]
        }, currentY);
        
        // Add decorative line under title
        pdf.setDrawColor(74, 144, 226);
        pdf.setLineWidth(1);
        pdf.line(margin, currentY, pageWidth - margin, currentY);
        currentY += 15; // Space after title
        
        // Reset text color for content
        pdf.setTextColor(51, 51, 51); // Dark gray for content

        // Add Section Text Content with improved formatting
        const textParts = section.textContent.split(/(\*\*.*?\*\*)/g).filter(part => part); // Split and remove empty parts

        // Indent the very first part if it's normal text
        if (textParts.length > 0 && !textParts[0].startsWith('**')) {
          // Add the first part with indentation
          currentY = addText("   " + textParts[0], { // Add indent, NO trim()
            fontSize: 11,
            lineHeight: 1.6
          }, currentY);
          // Process remaining parts starting from index 1
          for (let i = 1; i < textParts.length; i++) {
            const part = textParts[i];
            if (part.startsWith('**') && part.endsWith('**')) {
              const boldText = part.slice(2, -2);
              currentY = addText(boldText, {
                fontSize: 11,
                fontStyle: 'bold',
                color: [44, 44, 44]
              }, currentY);
            } else {
              // Add subsequent normal parts WITHOUT indentation and WITHOUT trim()
              currentY = addText(part, {
                fontSize: 11,
                lineHeight: 1.6
              }, currentY);
            }
          }
        } else {
          // If the text starts with bold or is empty, process all parts without initial indent
          for (let i = 0; i < textParts.length; i++) {
            const part = textParts[i];
            if (part.startsWith('**') && part.endsWith('**')) {
              const boldText = part.slice(2, -2);
              currentY = addText(boldText, {
                fontSize: 11,
                fontStyle: 'bold',
                color: [44, 44, 44]
              }, currentY);
            } else {
              // Add normal parts WITHOUT indentation and WITHOUT trim()
              currentY = addText(part, {
                fontSize: 11,
                lineHeight: 1.6
              }, currentY);
            }
          }
        }


        currentY += 20; // Space after text content

        // Add Section Image
        if (section.imageUrl && section.imageStatus === 'completed') {
          // Use cached image if available
          if (imageCache.has(section.id)) {
            const cachedImage = imageCache.get(section.id);
            if (cachedImage) {
              currentY = await addImage(cachedImage, currentY);
            } else {
              currentY = addText('[Image could not be loaded]', { fontSize: 9, fontStyle: 'italic' }, currentY);
            }
          } else if (section.imageUrl.includes('supabase.co/storage')) {
            // For Supabase URLs, use the URL directly
            currentY = await addImage(section.imageUrl, currentY);
          } else {
            currentY = addText('[Image could not be loaded]', { fontSize: 9, fontStyle: 'italic' }, currentY);
          }
        }
        currentY += 10; // Space after section
      }

      // Add footer with page numbers
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.setTextColor(150, 150, 150);
        pdf.text(
          `Sayfa ${i} / ${pageCount}`,
          pageWidth - margin - 50,
          pageHeight - 20
        );
      }

      // --- Save the PDF ---
      pdf.save(`Mimari_Blog_${new Date().toISOString().slice(0,10)}.pdf`);

    } catch (error) {
      console.error('Error generating PDF:', error);
      setGlobalError(`PDF generation failed: ${error.message}`);
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Call the standalone modal component */}
      <ImageCustomizationModal 
        open={imageCustomizationOpen}
        onClose={() => setImageCustomizationOpen(false)}
        roomTypes={roomTypes}
        roomStyles={roomStyles}
        colorPalettes={colorPalettes}
        selectedRoomType={selectedRoomType}
        setSelectedRoomType={setSelectedRoomType}
        selectedRoomStyle={selectedRoomStyle}
        setSelectedRoomStyle={setSelectedRoomStyle}
        selectedColorPalette={selectedColorPalette}
        setSelectedColorPalette={setSelectedColorPalette}
        onApply={applyCustomImageOptions}
        isApplying={isRerollingImage}
      />
      <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ 
        mb: 3, // Keep reduced bottom margin
        fontWeight: 600, // Slightly reduced weight for minimalism
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(45deg, #9b59b6, #3498db)' // Slightly adjusted warmer gradient
          : 'linear-gradient(45deg, #8e44ad, #2980b9)', // Adjusted light mode gradient
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        // filter removed for minimalism
        animation: `${warmProfessionalReveal} 1.5s cubic-bezier(0.25, 1, 0.5, 1) forwards`, // Apply new warm/professional animation
      }}>
        Mimari İçerik Oluşturucu
      </Typography>

      {/* Main Input Area - Styled like Gemini */}
      <Paper 
        elevation={6} 
        sx={{ 
          p: { xs: 2, sm: 3 }, // Responsive padding
          mb: 4, 
          borderRadius: '28px', // More rounded corners
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 40, 0.85)' : 'rgba(255, 255, 255, 0.9)', // Semi-transparent background
          backdropFilter: 'blur(10px)', // Frosted glass effect
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
          animation: `${fadeIn} 1.2s ease-out 0.2s`, // Delayed fade-in
          animationFillMode: 'backwards', // Start invisible before animation
          position: 'relative', // For positioning elements inside
          overflow: 'hidden', // Ensure glow doesn't leak too much
          transition: 'box-shadow 0.3s ease',
          '&:focus-within': { // Add glow when input is focused
            boxShadow: theme.palette.mode === 'dark' 
              ? `0 0 15px ${theme.palette.primary.main}50, 0 0 25px ${theme.palette.primary.main}30`
              : `0 0 15px ${theme.palette.primary.main}40`,
          }
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {/* Image Upload Area */}
          <Grid item xs={12} sm="auto">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Tooltip title="Görsel Yükle (Maks 4)">
                <IconButton
                  component="label"
                  color="primary"
                  disabled={isGenerating || sourceImages.length >= 4}
                  sx={{ 
                    border: `1px dashed ${theme.palette.divider}`, 
                    borderRadius: '50%', 
                    width: 48, 
                    height: 48,
                    transition: 'all 0.3s ease',
                    '&:hover': { 
                      backgroundColor: theme.palette.action.hover,
                      transform: 'scale(1.1)'
                    }
                  }}
                >
                  <CloudUploadIcon />
                  <VisuallyHiddenInput 
                    type="file" 
                    accept="image/*" 
                    multiple 
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                  />
                </IconButton>
              </Tooltip>
              {/* Image Previews */}
              {sourceImages.map((image, index) => (
                <Grow in={true} key={index}>
                  <Box 
                    sx={{ 
                      position: 'relative', 
                      width: 48, 
                      height: 48, 
                      borderRadius: '50%', // Circular previews
                      overflow: 'hidden',
                      boxShadow: 1,
                      border: `2px solid ${theme.palette.primary.light}`,
                      transition: 'transform 0.2s ease',
                      '&:hover': { transform: 'scale(1.1)' }
                    }}
                  >
                    <ImagePreview src={URL.createObjectURL(image)} alt={`Uploaded ${index}`} />
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -2, // Adjust position for circle
                        right: -2,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                        width: 20,
                        height: 20,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 0, 0, 0.8)', // Red on hover
                        },
                      }}
                      onClick={() => handleRemoveImage(index)}
                      disabled={isGenerating}
                    >
                      <DeleteIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                </Grow>
              ))}
            </Box>
          </Grid>

          {/* Text Input Field */}
          <Grid item xs>
            <TextField
              fullWidth
              variant="standard" // Use standard variant for cleaner look
              multiline
              maxRows={5} // Allow expansion but limit height
              value={userRequest}
              onChange={(e) => setUserRequest(e.target.value)}
              placeholder="Mimari bir rapor oluştur, malzeme seçimi ve mekan organizasyonuna odaklan..."
              disabled={isGenerating}
              InputProps={{
                disableUnderline: true, // Remove the default underline
                sx: { 
                  fontSize: '1rem', 
                  padding: '10px 0', // Adjust padding
                  lineHeight: 1.6,
                } 
              }}
              sx={{ 
                '& .MuiInputBase-root': { // Target the root for padding
                  padding: '0 !important', // Remove default padding if any
                },
              }}
            />
          </Grid>

          {/* Generate Button */}
          <Grid item xs={12} sm="auto">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Fab // Use Fab for the circular button look
                color="primary"
                size="medium" // Adjust size as needed
                onClick={handleGenerateDocument}
                disabled={isGenerating || !userRequest.trim() || showCreditWarning}
                sx={{
                  boxShadow: `0 0 10px ${theme.palette.primary.main}70`,
                  transition: 'all 0.3s ease',
                  animation: `${pulseGlow(theme.palette.primary.main)} 2s infinite ease-in-out`,
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: `0 0 15px ${theme.palette.primary.main}90`,
                  }
                }}
              >
                {isGenerating ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
              </Fab>
              {/* Animated Credit Chip - Moved here */}
              <Chip
                label={
                  <>
                    Kalan Kredi:&nbsp;
                    <CountUp
                      start={previousCredits}
                      end={creditsLeft}
                      duration={1.5} // Animation duration in seconds
                      separator="." // Optional: for thousands separator
                      decimals={0}
                    />
                  </>
                }
                color={creditsLeft < 20 ? "error" : "primary"}
                variant="outlined"
                sx={{
                  transition: 'background-color 0.3s ease-in-out, transform 0.3s ease-in-out',
                  ...(isAnimatingCredits && {
                    animation: 'creditFlash 0.6s ease-in-out',
                  }),
                  '@keyframes creditFlash': { // New animation: Subtle Shake
                    '0%, 100%': {
                      transform: 'translateX(0)'
                    },
                    '10%, 30%, 50%, 70%, 90%': {
                      transform: 'translateX(-2px)' // Quick left jiggle
                    },
                    '20%, 40%, 60%, 80%': {
                      transform: 'translateX(2px)' // Quick right jiggle
                    },
                  }
                }}
              />
            </Box>
            
            {/* Updated Credit Warning */}
            {showCreditWarning && (
              <Alert 
                severity="warning" 
                sx={{ mt: 2 }}
                onClose={() => {
                  setShowCreditWarning(false);
                  setGlobalError(null); // Clear related global error too
                }}
              >
                {globalError || "Yeterli krediniz yok. Lütfen aboneliğinizi yükseltin."} 
              </Alert>
            )}
            {/* </Box> <-- Removed this extra closing tag */}
          </Grid>
        </Grid>
      </Paper>

      {/* Example Prompts - Styled like Gemini suggestions */}
      {!isGenerating && documentSections.length === 0 && ( // Only show when not generating and no results yet
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          flexWrap: 'wrap', 
          gap: 2, 
          mb: 4,
          animation: `${fadeIn} 1.5s ease-out 0.5s`, // Delayed fade-in
          animationFillMode: 'backwards',
        }}>
          {[
            "Modern bir villa için detaylı bir mimari rapor oluştur.",
            "İskandinav tarzı bir yatak odası tasarımı öner.",
            "Endüstriyel bir mutfak için malzeme ve renk paleti seçenekleri sun.",
            "Küçük bir daire için minimalist yaşam alanı çözümleri geliştir."
          ].map((prompt, index) => (
            <Chip
              key={index}
              label={prompt}
              variant="outlined"
              onClick={() => setUserRequest(prompt)}
              sx={{
                cursor: 'pointer',
                borderRadius: '16px',
                padding: '8px 12px',
                transition: 'all 0.3s ease',
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)',
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 8px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'}`,
                }
              }}
            />
          ))}
        </Box>
      )}
      
      {/* Updated Credit Warning - Placed below input area */}
      {showCreditWarning && (
        <Alert 
          severity="warning" 
          sx={{ mt: -2, mb: 4 }} // Adjust margin
          onClose={() => {
            setShowCreditWarning(false);
            setGlobalError(null); // Clear related global error too
          }}
        >
          {globalError || "Yeterli krediniz yok. Lütfen aboneliğinizi yükseltin."} 
        </Alert>
      )}

      {showPlanApproval && (
        <Paper 
          elevation={2} 
          sx={{ 
            p: { xs: 2, sm: 2.5 }, 
            mb: 4, 
            borderRadius: 2, 
            animation: `${fadeIn} 0.5s ease-out`,
            border: '1px solid',
            borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(44, 62, 115, 0.1)',
            backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(26, 32, 53, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box 
              sx={{ 
                width: 4, 
                height: 24, 
                backgroundColor: '#2c3e73', 
                borderRadius: 1, 
                mr: 1.5 
              }} 
            />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                fontSize: '1.1rem',
                color: theme => theme.palette.mode === 'dark' ? '#fff' : '#2c3e73'
              }}
            >
              Önerilen Belge Yapısı
            </Typography>
          </Box>
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary', 
              mb: 2,
              fontSize: '0.85rem'
            }}
          >
            Aşağıdaki bölüm başlıklarını onaylayın veya düzenleyin. Başlıklar belgenizin yapısını oluşturacaktır.
          </Typography>
          
          {/* Show source images preview in a more compact way */}
          {sourceImages.length > 0 && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  fontSize: '0.8rem', 
                  fontWeight: 600,
                  mr: 1.5,
                  minWidth: '100px'
                }}
              >
                Kaynak Görseller:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 1, overflowX: 'auto', pb: 1 }}>
                {sourceImages.map((image, idx) => (
                  <Box 
                    key={idx} 
                    sx={{ 
                      width: 50, 
                      height: 50, 
                      borderRadius: 1,
                      flexShrink: 0,
                      overflow: 'hidden',
                      boxShadow: 1
                    }}
                  >
                    <img 
                      src={URL.createObjectURL(image)} 
                      alt={`Kaynak ${idx+1}`} 
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover' 
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          
          <Box sx={{ mb: 2 }}>
            {plannedSections.map((title, index) => {
              // Initialize section settings if they don't exist
              if (!plannedSectionSettings[index]) {
                const newSettings = [...plannedSectionSettings];
                newSettings[index] = {
                  roomType: selectedRoomType,
                  roomStyle: selectedRoomStyle,
                  colorPalette: selectedColorPalette
                };
                setPlannedSectionSettings(newSettings);
              }
              
              return (
                <Box 
                  key={index} 
                  sx={{ 
                    mb: 1.5, 
                    p: 1.5, 
                    border: '1px solid', 
                    borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(44, 62, 115, 0.1)', 
                    borderRadius: 1.5, 
                    position: 'relative',
                    backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(44, 62, 115, 0.1)' : 'rgba(44, 62, 115, 0.03)',
                    '&:hover': {
                      backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(44, 62, 115, 0.15)' : 'rgba(44, 62, 115, 0.05)',
                    }
                  }}
                >
                  <TextField
                    fullWidth
                    placeholder={`Bölüm ${index + 1}`}
                    InputProps={{ 
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="remove section"
                            onClick={() => handleRemovePlannedSection(index)}
                            size="small"
                            edge="end"
                            sx={{
                              color: 'text.secondary',
                              '&:hover': {
                                color: 'error.main',
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    value={title}
                    onChange={(e) => {
                      const newSections = [...plannedSections];
                      newSections[index] = e.target.value;
                      setPlannedSections(newSections);
                    }}
                    variant="outlined"
                    size="small"
                    sx={{ 
                      mb: 1.5,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.9)',
                        borderRadius: 1,
                        fontSize: '0.9rem',
                      }
                    }}
                  />
                  
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={4} sm={4} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontSize: '0.7rem', 
                            fontWeight: 600, 
                            mr: 1,
                            color: 'text.secondary',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Oda Tipi:
                        </Typography>
                        <select
                          value={plannedSectionSettings[index]?.roomType || selectedRoomType}
                          onChange={(e) => {
                            const newSettings = [...plannedSectionSettings];
                            if (!newSettings[index]) newSettings[index] = {};
                            newSettings[index].roomType = e.target.value;
                            setPlannedSectionSettings(newSettings);
                          }}
                          style={{ 
                            width: '100%', 
                            padding: '4px 8px', 
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                            fontSize: '0.8rem',
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.9)',
                          }}
                        >
                          {roomTypes.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.icon} {type.label}
                            </option>
                          ))}
                        </select>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={4} sm={4} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontSize: '0.7rem', 
                            fontWeight: 600, 
                            mr: 1,
                            color: 'text.secondary',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Oda Stili:
                        </Typography>
                        <select
                          value={plannedSectionSettings[index]?.roomStyle || selectedRoomStyle}
                          onChange={(e) => {
                            const newSettings = [...plannedSectionSettings];
                            if (!newSettings[index]) newSettings[index] = {};
                            newSettings[index].roomStyle = e.target.value;
                            setPlannedSectionSettings(newSettings);
                          }}
                          style={{ 
                            width: '100%', 
                            padding: '4px 8px', 
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                            fontSize: '0.8rem',
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.9)',
                          }}
                        >
                          {roomStyles.map(style => (
                            <option key={style.value} value={style.value}>
                              {style.icon} {style.label}
                            </option>
                          ))}
                        </select>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={4} sm={4} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontSize: '0.7rem', 
                            fontWeight: 600, 
                            mr: 1,
                            color: 'text.secondary',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Renk Paleti:
                        </Typography>
                        <select
                          value={plannedSectionSettings[index]?.colorPalette || selectedColorPalette}
                          onChange={(e) => {
                            const newSettings = [...plannedSectionSettings];
                            if (!newSettings[index]) newSettings[index] = {};
                            newSettings[index].colorPalette = e.target.value;
                            setPlannedSectionSettings(newSettings);
                          }}
                          style={{ 
                            width: '100%', 
                            padding: '4px 8px', 
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                            fontSize: '0.8rem',
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.9)',
                          }}
                        >
                          {colorPalettes.map(palette => (
                            <option key={palette.value} value={palette.value}>
                              {palette.label}
                            </option>
                          ))}
                        </select>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              );
            })}
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setPlannedSections([...plannedSections, "Yeni Bölüm"]);
              }}
              sx={{ 
                borderRadius: 1.5,
                borderColor: '#2c3e73',
                color: '#2c3e73',
                textTransform: 'none',
                fontSize: '0.8rem',
                '&:hover': {
                  borderColor: '#2c3e73',
                  backgroundColor: 'rgba(44, 62, 115, 0.05)',
                }
              }}
            >
              + Bölüm Ekle
            </Button>
            
            <Box>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setShowPlanApproval(false);
                  setPlannedSections([]);
                }}
                sx={{ 
                  mr: 1.5,
                  borderRadius: 1.5,
                  borderColor: 'text.secondary',
                  color: 'text.secondary',
                  textTransform: 'none',
                  fontSize: '0.8rem',
                }}
              >
                İptal
              </Button>
              
              <Button
                variant="contained"
                size="small"
                onClick={() => continueDocumentGeneration(plannedSections)}
                disabled={plannedSections.length === 0 || plannedSections.some(title => !title.trim())}
                sx={{ 
                  borderRadius: 1.5,
                  backgroundColor: '#2c3e73',
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  '&:hover': {
                    backgroundColor: '#1e2642',
                  }
                }}
              >
                Belge Oluşturmaya Başla
              </Button>
            </Box>
          </Box>
        </Paper>
      )}
      
      {/* Loading/Progress Indicator */}
      {isGenerating && (
        <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3, animation: `${fadeIn} 0.5s ease-out` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{currentStep}</Typography> 
          </Box>
          <Box sx={{ width: '100%', mb: 1 }}>
            <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3 }} />
          </Box>
          {/* Optional: Show generating section titles if needed */}
          {/* <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            {documentSections.filter(s => s.status === 'text_generating' || s.imageStatus === 'generating').map((section) => (
              <Chip key={section.id} label={section.title} size="small" variant="outlined" />
            ))}
          </Box> */}
        </Paper>
      )}
      
      {globalError && !showCreditWarning && ( // Avoid showing global error if credit warning is already visible
        <Alert severity="error" sx={{ mb: 4, animation: `${fadeIn} 0.5s ease-out` }}>
          {globalError}
        </Alert>
      )}
      
      {documentSections.length > 0 && (
        <Box ref={resultContainerRef}>
          {/* Display Total Consumed Credits Message */}
          {/* Display Total Consumed Credits Message */}
          {showTotalConsumedMessage && totalCreditsConsumedThisSession > 0 && (
            <Grow in={showTotalConsumedMessage} timeout={800}>
              <Alert 
                severity="success" // Changed to success for positive feedback
                variant="outlined" // Use outlined style
                icon={<CheckCircleOutlineIcon fontSize="inherit" />} // Add check icon
                sx={{ 
                  mb: 3, 
                  borderColor: 'success.light', // Use theme colors
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(102, 187, 106, 0.1)' : 'rgba(102, 187, 106, 0.05)', // Subtle background
                }} 
                onClose={() => setShowTotalConsumedMessage(false)} // Allow closing
              >
                Belge oluşturuldu! Bu işlem için toplam {totalCreditsConsumedThisSession} kredi kullanıldı.
              </Alert>
            </Grow>
          )}

          {/* Results Header Area */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 3
          }}>
            {isEditingTitle ? (
              <TextField
                value={documentTitle}
                onChange={(e) => setDocumentTitle(e.target.value)}
                onBlur={() => setIsEditingTitle(false)} // Save on blur
                autoFocus
                variant="standard"
                sx={{ 
                  width: 'fit-content', // Adjust width dynamically
                  '& .MuiInput-input': { 
                    fontSize: '1.5rem', // Match h5 size
                    fontWeight: 'bold', // Match h5 weight
                    paddingBottom: '4px' // Align baseline
                  } 
                }}
              />
            ) : (
              <Box 
                onClick={() => setIsEditingTitle(true)} 
                sx={{ 
                  display: 'inline-flex', // Use inline-flex for alignment
                  alignItems: 'center', 
                  cursor: 'pointer', 
                  '&:hover': { backgroundColor: theme.palette.action.hover },
                  '& .edit-icon': { opacity: 0, transition: 'opacity 0.2s' }, // Hide icon initially
                  '&:hover .edit-icon': { opacity: 0.6 } // Show icon on hover
                }}
              >
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom 
                  sx={{ 
                    mr: 0.5, // Space between text and icon
                    mb: 0 // Remove default gutterBottom from Typography
                  }} 
                >
                  {documentTitle}
                </Typography>
                <EditIcon 
                  className="edit-icon"
                  fontSize="inherit" // Inherit size from Typography
                  sx={{ 
                    color: 'text.secondary',
                  }} 
                />
              </Box>
            )}
            
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadDocument}
              disabled={isDownloadingPdf} // Disable while PDF is downloading
            >
              HTML İndir
            </Button>
            
            <Button
              variant="contained" // Keep contained for primary action
              startIcon={isDownloadingPdf ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdfIcon />}
              onClick={handleDownloadPdf}
              disabled={isDownloadingPdf} // Disable while downloading
              sx={{ 
                ml: 2, // Add margin left
                background: theme.palette.mode === 'dark' 
                  ? 'linear-gradient(45deg, #6a1b9a, #303f9f)' // Darker gradient for PDF
                  : undefined, // Use default theme gradient or color for light mode
                boxShadow: theme.shadows[3],
                '&:hover': {
                  boxShadow: theme.shadows[5],
                }
              }} 
            >
              {isDownloadingPdf ? 'Oluşturuluyor...' : 'PDF İndir'}
            </Button>
          </Box>

          {/* Logo Export Options */}
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3, backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)', border: `1px solid ${theme.palette.divider}` }}>
            <FormControlLabel
              control={
                <Switch
                  checked={includeLogoInExport}
                  onChange={(e) => setIncludeLogoInExport(e.target.checked)}
                  disabled={userLogos.length === 0 || logoLoading}
                />
              }
              label="Logoyu Dışa Aktarmaya Ekle"
              sx={{ mb: userLogos.length === 0 ? 0 : 1 }}
            />
            {userLogos.length === 0 && !logoLoading && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                Henüz logo yüklenmemiş. Profil ayarlarından ekleyebilirsiniz.
              </Typography>
            )}
            {includeLogoInExport && userLogos.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 4 }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="logo-select-label">Logo Seç</InputLabel>
                  <Select
                    labelId="logo-select-label"
                    value={selectedLogoUrl}
                    label="Logo Seç"
                    onChange={(e) => setSelectedLogoUrl(e.target.value)}
                    disabled={logoLoading}
                  >
                    {logoLoading ? (
                      <MenuItem value="">
                        <CircularProgress size={20} /> Yükleniyor...
                      </MenuItem>
                    ) : (
                      userLogos.map((logo) => (
                        <MenuItem key={logo.id} value={logo.logo_url}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img src={logo.logo_url} alt={logo.logo_name || 'logo'} style={{ width: 20, height: 20, marginRight: 8, objectFit: 'contain' }} />
                            {logo.logo_name || `Logo ${logo.id.substring(0, 6)}`}
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                {selectedLogoUrl && (
                  <Avatar
                    src={selectedLogoUrl}
                    variant="rounded"
                    sx={{ width: 40, height: 40, bgcolor: 'transparent' }}
                  >
                    L
                  </Avatar>
                )}
              </Box>
            )}
          </Paper>
          
          {documentSections.map((section, index) => (
            /* Replace Grow with Collapse */
            <Collapse in={section.status !== 'pending'} key={section.id} timeout={500 + index * 100}> {/* Staggered animation */}
              <div> {/* This div is the direct child of Collapse */}
                <SectionPaper elevation={3} id={section.id} sx={{ borderRadius: 3 }}> {/* More rounded */}
                  {/* Inline Title Editing */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {editingElement?.sectionId === section.id && editingElement?.field === 'title' ? (
                    <TextField
                      value={section.title}
                      onChange={(e) => handleSectionUpdate(section.id, 'title', e.target.value)}
                      onBlur={() => setEditingElement(null)}
                      autoFocus
                      variant="standard"
                      fullWidth
                      sx={{ 
                        mb: 0, // Remove bottom margin
                        '& .MuiInput-input': { 
                          fontSize: '1.3rem', // Slightly larger title
                          fontWeight: 600, 
                          color: theme.palette.primary.main, 
                          paddingBottom: '4px' 
                        } 
                      }}
                    />
                  ) : (
                    <Box 
                      onClick={() => setEditingElement({ sectionId: section.id, field: 'title' })}
                      sx={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        cursor: 'pointer', 
                        borderRadius: 1,
                        p: 0.5, // Add padding for hover effect area
                        mx: -0.5, // Counteract padding
                        transition: 'background-color 0.2s ease',
                        '&:hover': { backgroundColor: theme.palette.action.hover },
                        '& .edit-icon': { opacity: 0, transition: 'opacity 0.2s', ml: 0.5 }, // Add margin-left
                        '&:hover .edit-icon': { opacity: 0.7 } 
                      }}
                    >
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                          fontSize: '1.3rem', // Match TextField size
                          mb: 0 // Remove default gutterBottom
                        }}
                      >
                        {section.title}
                      </Typography>
                      <EditIcon 
                        className="edit-icon"
                        fontSize="small" // Use small icon
                        sx={{ color: 'text.secondary' }} 
                      />
                    </Box>
                  )}
                  {/* Reroll Text Button */}
                  <Tooltip title="Metni Yeniden Oluştur (5 Kredi)">
                    <IconButton
                      size="small"
                      onClick={() => regenerateSectionText(section.id)}
                      disabled={isGenerating || section.status === 'text_generating'}
                      sx={{ 
                        ml: 1, 
                        color: 'text.secondary',
                        '&:hover': { color: 'primary.main' }
                      }}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                
                <Divider sx={{ mb: 2, mt: 1 }} />
                
                {/* Text Content Area */}
                {section.status === 'text_generating' && !section.textContent ? ( // Show skeleton only if text is empty
                  <Box sx={{ mb: 2 }}>
                    <Skeleton variant="text" height={20} animation="wave" />
                    <Skeleton variant="text" height={20} animation="wave" sx={{ mb: 1 }}/>
                    <Skeleton variant="text" height={20} animation="wave" sx={{ mb: 1 }}/>
                    <Skeleton variant="text" height={20} animation="wave" width="80%" />
                  </Box>
                ) : section.error && section.status === 'error' ? ( // Show error only if status is error
                  <Box sx={{ mb: 2 }}>
                    <Alert 
                      severity="error" 
                      sx={{ mb: 2 }}
                      // Action is now part of the title area
                    >
                      {section.error}
                    </Alert>
                  </Box>
                ) : ( // Render text content (even if generating, it will stream)
                  <Box sx={{ mb: 2, position: 'relative' }}>
                    {editingElement?.sectionId === section.id && editingElement?.field === 'content' ? (
                      <TextField
                        value={section.textContent} // Keep showing text while editing
                        onChange={(e) => handleSectionUpdate(section.id, 'textContent', e.target.value)}
                        onBlur={() => setEditingElement(null)}
                        autoFocus
                        multiline
                        fullWidth
                        variant="outlined"
                        rows={Math.max(5, section.textContent.split('\n').length)} // Adjust rows based on content
                        sx={{ 
                          '& .MuiOutlinedInput-root': { padding: '10px' },
                          '& .MuiOutlinedInput-input': { lineHeight: 1.7, fontSize: '1rem' } // Slightly larger font, more line height
                        }}
                      />
                    ) : (
                      <Box 
                        sx={{ 
                          position: 'relative', 
                          lineHeight: 1.7, // Apply line height to container
                          fontSize: '1rem', // Apply font size
                          '&:hover .edit-content-icon': { opacity: 0.7 }, // Show icon on hover
                          minHeight: '60px', // Ensure some height even when empty or generating
                        }}
                      >
                        {/* Render Markdown only when completed AND content is not empty */}
                        {section.status === 'completed' && String(section.textContent || '').trim() ? (
                          /* Render Markdown */
                          <ReactMarkdown>
                            {String(section.textContent || '').trim()}
                          </ReactMarkdown>
                        ) : (
                          /* Render plain text or placeholder otherwise (generating, pending, error, or empty) */
                          <Typography 
                            variant="body1" 
                            component="div" // Use div to allow multi-line
                            sx={{ whiteSpace: 'pre-wrap' }} // Preserve line breaks from stream
                          >
                            {section.textContent || '\u00A0'} {/* Show streaming/existing text or placeholder */}
                          </Typography>
                        )}
                        {/* Edit Icon - positioned better */}
                        <Tooltip title="İçeriği Düzenle">
                          <IconButton
                            className="edit-content-icon"
                            size="small"
                            onClick={() => setEditingElement({ sectionId: section.id, field: 'content' })}
                            sx={{
                              position: 'absolute',
                              top: -5, // Position slightly above
                              right: -5,
                              opacity: 0, 
                              transition: 'opacity 0.2s ease',
                              color: 'text.secondary',
                              backgroundColor: theme.palette.background.paper + '99', // Semi-transparent background
                              '&:hover': {
                                backgroundColor: theme.palette.action.hover, 
                                opacity: 1, 
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                )}
                
                {/* Image Area */}
                {section.imageStatus === 'generating' && (
                  <Skeleton 
                    variant="rectangular" 
                    height={350} // Slightly taller skeleton
                    animation="wave" 
                    sx={{ borderRadius: 3, mb: 2 }} // More rounded
                  />
                )}
                
                {section.imageStatus === 'completed' && section.imageUrl && (
                  <Grow in={true} timeout={800}>
                    <Box sx={{ position: 'relative', mb: 2, mt: 1 }}>
                      <Box 
                        sx={{ 
                          borderRadius: 3, // More rounded
                          overflow: 'hidden',
                          boxShadow: `0 8px 20px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.15)'}`,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center', // Center image vertically if needed
                          position: 'relative',
                          background: theme.palette.mode === 'dark' ? '#222' : '#f9f9f9', // Subtle background
                          minHeight: '200px', // Ensure minimum height
                          '&:hover .image-controls': { opacity: 1 }
                        }}
                      >
                        <img 
                          src={section.imageUrl} 
                          alt={`${section.title} visualization`}
                          style={{ 
                            display: 'block', // Ensure image behaves like a block
                            maxWidth: '100%', 
                            maxHeight: '500px', // Increase max height
                            objectFit: 'contain',
                            borderRadius: 'inherit' // Inherit border radius
                          }}
                        />
                        
                        {/* Image controls overlay - Improved Style */}
                        <Box 
                          className="image-controls"
                          sx={{
                            position: 'absolute',
                            bottom: 8, // Position slightly from bottom
                            left: '50%', // Center horizontally
                            transform: 'translateX(-50%)', // Precise centering
                            padding: '6px 12px',
                            background: 'rgba(0, 0, 0, 0.7)',
                            backdropFilter: 'blur(5px)',
                            borderRadius: '20px', // Pill shape
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            opacity: 0,
                            transition: 'opacity 0.3s ease',
                          }}
                        >
                          <Button
                            variant="contained"
                            size="small"
                            color="primary"
                            startIcon={<EditIcon />}
                            onClick={() => openImageCustomization(section.id)}
                            sx={{ 
                              borderRadius: '16px',
                              fontSize: '0.75rem',
                              textTransform: 'none',
                              boxShadow: 'none',
                              backgroundColor: 'rgba(25, 118, 210, 0.8)',
                              '&:hover': { backgroundColor: 'rgba(25, 118, 210, 1)' }
                            }}
                          >
                            Özelleştir
                          </Button>
                          
                          <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                          
                          <Tooltip title="Yeniden Oluştur (5 Kredi)">
                            <IconButton
                              size="small"
                              onClick={() => regenerateSectionImage(section.id)}
                              disabled={isRerollingImage || isGenerating}
                              sx={{ 
                                color: 'white',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' }
                              }}
                            >
                              {isRerollingImage && currentCustomizingSection?.id === section.id ? 
                                <CircularProgress size={16} color="inherit" /> : 
                                <RefreshIcon sx={{ fontSize: 16 }} />
                              }
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      
                      {/* Room type and style tags - Moved below image */}
                      <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                        {section.roomType && roomTypes.find(t => t.value === section.roomType) && (
                          <Chip 
                            icon={<span style={{ marginRight: '4px' }}>{roomTypes.find(t => t.value === section.roomType)?.icon}</span>}
                            size="small" 
                            label={roomTypes.find(t => t.value === section.roomType)?.label} 
                            variant="outlined"
                            sx={{ borderRadius: '16px' }}
                          />
                        )}
                        {section.roomStyle && roomStyles.find(s => s.value === section.roomStyle) && (
                          <Chip 
                            icon={<span style={{ marginRight: '4px' }}>{roomStyles.find(s => s.value === section.roomStyle)?.icon}</span>}
                            size="small" 
                            label={roomStyles.find(s => s.value === section.roomStyle)?.label} 
                            variant="outlined"
                            sx={{ borderRadius: '16px' }}
                          />
                        )}
                         {section.colorPalette && colorPalettes.find(p => p.value === section.colorPalette) && (
                          <Chip 
                            avatar={
                              <Box sx={{ display: 'flex', gap: 0.2, ml: '4px' }}>
                                {colorPalettes.find(p => p.value === section.colorPalette)?.colors.slice(0, 3).map((color, i) => (
                                  <Box key={i} sx={{ width: 8, height: 8, bgcolor: color, borderRadius: '50%' }}/>
                                ))}
                              </Box>
                            }
                            size="small" 
                            label={colorPalettes.find(p => p.value === section.colorPalette)?.label} 
                            variant="outlined"
                            sx={{ borderRadius: '16px' }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Grow>
                )}
                
                {section.imageStatus === 'error' && (
                  <Box sx={{ mb: 2, mt: 1 }}>
                    <Alert 
                      severity="warning" // Use warning for image errors unless critical
                      icon={<ErrorOutlineIcon fontSize="inherit" />}
                      action={
                        <Tooltip title="Görseli yeniden oluştur (5 Kredi)">
                          <IconButton
                            color="inherit"
                            size="small"
                            onClick={() => regenerateSectionImage(section.id)}
                            disabled={isRerollingImage || isGenerating}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      }
                      sx={{ borderRadius: 2 }}
                    >
                      Görsel oluşturulamadı. ({section.error?.substring(0, 100)}...)
                    </Alert>
                  </Box>
                )}
                </SectionPaper>
              </div>
            </Collapse>
          ))}
        </Box>
      )}
    </Container>
  );
};

export default DocumentGenerator;
